import React, { useState, useEffect, useRef, useCallback, useMemo, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { VoiceService, parseVoiceCommand } from '@services/voice';
import { TelegramVoiceService } from '@services/telegram-voice';
import { NavigationService } from '@services/navigation';
import { telegram } from '@services/telegram';
import { offlineService } from '@services/offline';
import { speechService } from '@services/speech';
import { cacheService } from '@services/cache';
import { parseCompactWellsStream } from '@services/compactParser';
import { useAppStore } from '@stores/appStore';
import { feedbackService } from '../services/feedback';
import { requestPreciseLocation } from '@services/highPrecisionLocation';
import type { Well, Basin } from '../types';
import { BASINS, NEW_MEXICO_COUNTIES } from '../types';
import { getCachedBasinForPoint } from '@services/basins';
import { Box, Container, Typography, Paper, Button, Fade, Chip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemText, IconButton, Snackbar, Alert, Drawer, Divider, TextField, InputAdornment, CircularProgress } from '@mui/material';
import CyberSpinner from './CyberSpinner';
// COMMENTED: MicIcon and ErrorIcon no longer used in canvas UI
// import MicIcon from '@mui/icons-material/Mic';
import MapIcon from '@mui/icons-material/Map';
// import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import WifiOffIcon from '@mui/icons-material/WifiOff';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupIcon from '@mui/icons-material/Group';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import MicIcon from '@mui/icons-material/Mic';
import StarIcon from '@mui/icons-material/Star';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// import RouteIcon from '@mui/icons-material/Route'; // Removed - Caliche Routes section deleted
// MUI Icons for facility types
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterIcon from '@mui/icons-material/Water';
import ConstructionIcon from '@mui/icons-material/Construction';
import BoltIcon from '@mui/icons-material/Bolt';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SettingsIcon from '@mui/icons-material/Settings';
import FactoryIcon from '@mui/icons-material/Factory';
import OpacityIcon from '@mui/icons-material/Opacity';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import ScienceIcon from '@mui/icons-material/Science';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AdjustIcon from '@mui/icons-material/Adjust';
import HubIcon from '@mui/icons-material/Hub';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import LandscapeIcon from '@mui/icons-material/Landscape';
import SpeedIcon from '@mui/icons-material/Speed';
import AirIcon from '@mui/icons-material/Air';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import RecyclingIcon from '@mui/icons-material/Recycling';
import HealingIcon from '@mui/icons-material/Healing';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/Warning';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LockIcon from '@mui/icons-material/Lock';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FastfoodIcon from '@mui/icons-material/Fastfood';

import Card from '@mui/material/Card';
import { SearchResults } from './SearchResults';
import { FavoritesPanel } from './FavoritesPanel';
import { FavoritesMapView } from './FavoritesMapView';
import { OnboardingDialog } from './OnboardingDialog';
import { AddFacilityForm } from './AddFacilityForm';
import { CommunityRoutes } from './CommunityRoutes';
import { CommunityRoutesPanel } from './CommunityRoutesPanel';
import CrewPanel from './CrewPanel';
import { WellBrowser } from './WellBrowser';
import { ActiveOperations } from './ActiveOperations';
import { CommunityFacilitiesFab } from './CommunityFacilitiesFab';
import { FacilitiesViewPanel } from './FacilitiesViewPanel';
import { HazardsViewPanel } from './HazardsViewPanel';
import { PhotoUploader } from './PhotoUploader';
import AISpeedDial from './AISpeedDial';
import { UnifiedFabManager } from './UnifiedFabManager';
import { CharityDashboard } from './CharityDashboard';
import { roadHazardsService, RoadHazard } from '../services/roadHazards';
import SuggestionsDialog from './SuggestionsDialog';
import QRShareDialog from './QRShareDialog';
import ServicesMarketplace from './ServicesMarketplace';
import JobsBoard from './JobsBoard';
import FriendsPanel from './FriendsPanel';
import { MorningPulse } from './MorningPulse';
import { BasinFeed } from './BasinFeed';
import { OfflineCacheManager } from './OfflineCacheManager';
import { getCyberColors } from '../theme/cyberColors';
import { useTranslation } from '../i18n/translations';
// import { VPNWarningSnackbar } from './VPNWarningSnackbar'; // Temporarily disabled
import { DrawOverAppsPrompt, useDrawOverAppsPrompt } from './DrawOverAppsPrompt';
import { SubscriptionPanel } from './SubscriptionPanel';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { featureAccess } from '../services/featureAccess';
import { ExpiredTrialBanner } from './ExpiredTrialBanner';
import { LocationPermissionDialog, useLocationPermission } from './LocationPermissionDialog';
import { useLocationExpiryReminder } from '../services/locationExpiryReminder';
// import Badge from '@mui/material/Badge'; // Temporarily disabled with Basin FABs

type VoiceStatus = 'idle' | 'listening' | 'processing' | 'success' | 'error';

const RECENT_SEARCH_LIMIT = 20;

/* Basin Channels - Temporarily disabled
const BASIN_CHANNELS = [
  { key: 'permian', name: 'Permian Basin', short: 'P', link: 'https://t.me/+ben7lhimHw80YWIx', color: '#ec4899' },
  { key: 'delaware', name: 'Delaware Basin', short: 'D', link: 'https://t.me/+bEpWNowuqHoyNzMx', color: '#22c55e' },
  { key: 'san-juan', name: 'San Juan Basin', short: 'SJ', link: 'https://t.me/+pISC4pfyjmtmOGI5', color: '#f97316' },
  { key: 'raton', name: 'Raton Basin', short: 'R', link: 'https://t.me/+zXCjcMcTn4tiMjAx', color: '#3b82f6' }
];
*/

// Helper function to map county to basin
function getBasinForCounty(county: string): string {
  if (!county) return 'Unknown';
  const countyInfo = NEW_MEXICO_COUNTIES.find(c => 
    c.name.toLowerCase().trim() === county.toLowerCase().trim()
  );
  return countyInfo?.basin || county;
}

// Normalize various facility payload shapes into the Well interface we display
const mapFacilityToWell = (facility: any): Well => {
  // Generate a unique ID if missing
  const ensureId = (f: any): string => {
    if (f.id) return String(f.id);
    // Fallback: create ID from API/identifier + coordinates
    const identifier = f.api || f.identifier || f.apiNumber || f.permitNumber || '';
    return `facility-${identifier}-${f.latitude}-${f.longitude}`;
  };

  // Check if this is already a Well object (has both api and wellName)
  if (facility.api && facility.wellName) {
    return {
      ...facility,
      id: ensureId(facility)
    } as Well;
  }

  // Get the county from various possible fields
  const county = facility.county || facility.district || 'Unknown';
  const basinFromGeo = getCachedBasinForPoint(facility.latitude, facility.longitude);

  // Map the well name - API returns 'name' for facilities, wells have 'wellName'
  const wellName = facility.wellName || facility.name || facility.facilityName || 'Unknown';
  
  // Map the API number - API returns 'identifier' for wells, or use other fields
  const api = facility.api || facility.identifier || facility.permitNumber || facility.apiNumber || '';
  
  // Map operator - API returns 'operator' or 'operatorName'
  const operator = facility.operator || facility.operatorName || 'Unknown';
  
  // Map status
  const status = facility.status || 'Unknown';
  
  // Map state (TX or NM) - defaults to NM for backward compatibility
  const state = facility.state || 'NM';

  return {
    id: ensureId(facility),
    api,
    wellName,
    latitude: facility.latitude,
    longitude: facility.longitude,
    county,
    state,
    operator,
    status,
    basin: facility.basin || basinFromGeo || getBasinForCounty(county),
    distance: facility.distance,
    // Preserve the backend type (well, swd, rig, facility) for icon rendering
    ...(facility.type ? { type: facility.type } : {}),
    // Preserve facilityType for subtype icons (Tank Battery, Brine Well, etc.)
    ...(facility.facilityType ? { facilityType: facility.facilityType } : {})
  };
};

export function VoiceNavigation() {
  // Vibrant colors array for fingerprint
  const vibrantColors = [
    '#00ffff', // Cyan
    '#ff00ff', // Magenta
    '#00ff00', // Green
    '#ff6b6b', // Coral
    '#ffd93d', // Yellow
    '#6bcb77', // Mint
    '#4d96ff', // Blue
    '#ff9f43', // Orange
    '#a66cff', // Purple
    '#ff61d2', // Pink
    '#72efdd', // Teal
    '#ffc75f', // Gold
  ];

  // Random fingerprint color - changes each page visit for fun visual variety
  const fingerprintColor = useMemo(() => {
    return vibrantColors[Math.floor(Math.random() * vibrantColors.length)];
  }, []);

  // Active fingerprint color - changes each time you tap
  const [activeFingerprintColor, setActiveFingerprintColor] = useState(() => 
    vibrantColors[Math.floor(Math.random() * vibrantColors.length)]
  );

  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [transcript, setTranscript] = useState('');
  const [editableQuery, setEditableQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showBasinDialog, setShowBasinDialog] = useState(false);
  const [showCountyDialog, setShowCountyDialog] = useState(false);
  const [searchResults, setSearchResults] = useState<Well[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showWellBrowser, setShowWellBrowser] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isFacilityTypeLoading, setIsFacilityTypeLoading] = useState(false); // Full-screen CyberSpinner for facility type clicks
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showFavorites, setShowFavorites] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  // @ts-ignore - setSelectedFacilityType used in handleFacilityTypeClick but value only used for logging
  const [selectedFacilityType, setSelectedFacilityType] = useState<string>('all');
  const [selectedFacilityLabel, setSelectedFacilityLabel] = useState<string>('Show All');
  const [showOnboarding, setShowOnboarding] = useState(false);
  // cacheInfo state kept for potential future display - updated by download handlers
  const [, setCacheInfo] = useState<{ lastSync?: Date; size: number } | null>(null);
  const [searchCacheInfo, setSearchCacheInfo] = useState<{ count: number; time: number; cached: boolean } | null>(null);
  // Pagination state for infinite scroll
  const [searchTotalCount, setSearchTotalCount] = useState<number>(0);
  const [searchHasMore, setSearchHasMore] = useState<boolean>(false);
  const [searchOffset, setSearchOffset] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const searchQueryRef = useRef<string>(''); // Track current search for pagination
  const facilityTypeLoadModeRef = useRef<string | null>(null); // Track if in facility type "all" mode for Load More
  // @ts-ignore - used in JSX menu
  const [showDownloadCountyDialog, setShowDownloadCountyDialog] = useState(false);
  // @ts-ignore - used in JSX menu
  const [showDownloadBasinDialog, setShowDownloadBasinDialog] = useState(false);
  // Track no results for inline display
  // @ts-ignore - setter used in handlers
  const [noResultsQuery, setNoResultsQuery] = useState<string | null>(null);
  // Basin download state - managed by OfflineCacheManager
  const [basinDownloadLoading] = useState({
    delaware: false,
    permian: false,
    'san-juan': false,
    raton: false
  });
  const [downloadedBasins, setDownloadedBasins] = useState({
    delaware: false,
    permian: false,
    'san-juan': false,
    raton: false
  });
  const [, setIntegrityVerified] = useState(false);
  const [expandHelp, setExpandHelp] = useState(false); // Help section in drawer
  const [openDrawer, setOpenDrawer] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [showAddFacility, setShowAddFacility] = useState(false);
  const [addFacilityLocation, setAddFacilityLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showCommunityRoutes, setShowCommunityRoutes] = useState(false);
  const [showCaliches, setShowCaliches] = useState(false); // Caliche Routes panel
  const [calicheRoutesRefreshKey, setCalicheRoutesRefreshKey] = useState(0);
  const [showFacilitiesView, setShowFacilitiesView] = useState(false); // Facilities View panel
  const [showHazardsView, setShowHazardsView] = useState(false); // Hazards View panel
  const [showFacilityPhotoUploader, setShowFacilityPhotoUploader] = useState(false); // Facility photo uploader
  const [facilityForPhoto, setFacilityForPhoto] = useState<{ id: string; name: string; type: string; latitude: number; longitude: number } | null>(null);
  const [showCommunityFacilities, setShowCommunityFacilities] = useState(false);
  const [showSettingsPopout, setShowSettingsPopout] = useState(false); // Show settings mini FABs
  const [showCrews, setShowCrews] = useState(false);
  const [showActiveOperations, setShowActiveOperations] = useState(false);
  const [showServices, setShowServices] = useState(false); // Services finder dialog
  const [showJobsBoard, setShowJobsBoard] = useState(false); // Jobs board dialog
  const [showFriends, setShowFriends] = useState(false); // Friends panel dialog
  const [showBasinCommunity, setShowBasinCommunity] = useState(false); // Basin community panel (Morning Pulse + Feed)
  const [showSearchMode, setShowSearchMode] = useState(false); // Search Mode full-screen dialog
  const [facilityRegion, setFacilityRegion] = useState<'NM' | 'TX' | null>(null);
  const [showManualSearch, setShowManualSearch] = useState(false); // Manual Search dialog with gradient chips
  const [manualSearchQuery, setManualSearchQuery] = useState(''); // Manual search input
  // Premium/Subscription
  const [showSubscription, setShowSubscription] = useState(false); // Subscription panel dialog
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  // Road hazards - real-time reporting
  const [roadHazards, setRoadHazards] = useState<RoadHazard[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false); // Suggestions dialog
  const [showQRShare, setShowQRShare] = useState(false); // QR share/referral dialog
  const [showCharityDashboard, setShowCharityDashboard] = useState(false); // ZiaGivesBack charity dashboard
  const [showOfflineCacheManager, setShowOfflineCacheManager] = useState(false); // Unified offline cache manager
  const [networkStatus] = useState<'online' | 'offline' | 'poor'>('online'); // Network status
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  // Draw over apps prompt for Telegram Android users
  const { showPrompt: showDrawOverAppsPrompt, dismissPrompt: dismissDrawOverAppsPrompt } = useDrawOverAppsPrompt();
  // Location permission dialog hook
  const locationPermission = useLocationPermission();
  // Location expiry reminder hook - shows toast when permission is about to expire
  const { expiryWarning, dismissExpiryWarning, requestLocationAgain } = useLocationExpiryReminder();
  // @ts-ignore - setter used in handleSearchCommand
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  // Rich search history with facility breakdown for gradient chips
  const [searchHistory, setSearchHistory] = useState<Array<{
    query: string;
    facilityBreakdown: Record<string, number>;
    totalCount: number;
    timestamp: number;
  }>>([]);
  // @ts-ignore - setter used in voice init
  const [voiceAvailable, setVoiceAvailable] = useState<boolean>(true); // Track if voice is available
  const [isInTelegramApp, setIsInTelegramApp] = useState<boolean>(false); // Track if in Telegram Mini App
  // COMMENTED: search input removed from main page canvas
  // const searchInputRef = useRef<HTMLInputElement>(null); // Ref for search input focus
  /* Temporarily disabled with Basin FABs
  const [channelBadges, setChannelBadges] = useState<Record<string, number>>({
    permian: 0,
    delaware: 0,
    'san-juan': 0,
    raton: 0
  });
  const channelUnreadUrl = (import.meta as any).env?.VITE_CHANNEL_UNREAD_URL || '/api/channel-unread';
  */
  const voiceService = useRef<VoiceService | TelegramVoiceService | null>(null);
  const useTelegramVoice = useRef<boolean>(false); // Will be set based on API availability

  // Global store selectors
  const selectedCounty = useAppStore((state) => state.selectedCounty);
  const setSelectedCounty = useAppStore((state) => state.setSelectedCounty);
  const favorites = useAppStore((state) => state.favorites);
  const addFavoriteToStore = useAppStore((state) => state.addFavorite);
  const removeFavoriteFromStore = useAppStore((state) => state.removeFavorite);
  const isFavorite = useAppStore((state) => state.isFavorite);
  const setSelectedWell = useAppStore((state) => state.setSelectedWell);
  const voiceFeedbackEnabled = useAppStore((state) => state.voiceFeedbackEnabled);
  const isOffline = useAppStore((state) => state.isOffline);
  const setIsOffline = useAppStore((state) => state.setIsOffline);
  // simulateOffline removed - green pill UI was removed per user request
  const nearbyLocation = useAppStore((state) => state.nearbyLocation);
  const setNearbyLocation = useAppStore((state) => state.setNearbyLocation);
  const sharedRoutePhotoId = useAppStore((state) => state.sharedRoutePhotoId);
  const sharedFacilityId = useAppStore((state) => state.sharedFacilityId);
  const setSharedFacilityId = useAppStore((state) => state.setSharedFacilityId);
  const showFavoritesMap = useAppStore((state) => state.showFavoritesMap);
  const setShowFavoritesMap = useAppStore((state) => state.setShowFavoritesMap);
  const darkMode = useAppStore((state) => state.darkMode);
  const toggleDarkMode = useAppStore((state) => state.toggleDarkMode);
  const language = useAppStore((state) => state.language);
  const toggleLanguage = useAppStore((state) => state.toggleLanguage);

  // Basin resolver retained for future use (currently unused)

  // ==================== Gradient Search Chips - Colors & Helpers ====================
  
  // Facility type colors for gradient chips - ALL UNIQUE for click-to-filter
  const FACILITY_COLORS: Record<string, string> = {
    well: '#f59e0b',           // Amber
    wells: '#f59e0b',          // Amber (alias)
    swd: '#10b981',            // Emerald Green
    swds: '#10b981',           // Emerald Green (alias)
    rig: '#fbbf24',            // Yellow/Gold
    rigs: '#fbbf24',           // Yellow/Gold (alias)
    facility: '#ec4899',       // Pink (changed from blue)
    facilities: '#ec4899',     // Pink (alias)
    tank_battery: '#06b6d4',   // Cyan
    flare_stack: '#ef4444',    // Red
    compressor: '#8b5cf6',     // Purple
    gas_plant: '#a855f7',      // Violet
    pipeline: '#0ea5e9',       // Sky Blue
    pump_station: '#14b8a6',   // Teal
    meter_station: '#f97316',  // Orange
    injection_well: '#22c55e', // Green (distinct)
    water_source: '#38bdf8',   // Light Blue
    treating: '#d946ef',       // Fuchsia
    refinery: '#84cc16',       // Lime
    storage: '#fb923c',        // Light Orange
    brine: '#2dd4bf',          // Turquoise
    disposal: '#4ade80',       // Light Green
    saltwater: '#34d399',      // Medium Green
    oil: '#854d0e',            // Brown/Oil color
    gas: '#7c3aed',            // Indigo
    unknown: '#94a3b8',        // Gray
    taco_truck: '#d946ef',     // Vibrant magenta for tacos
    taco: '#d946ef',           // Alias for taco results
    tacos: '#d946ef',          // Alias for taco results
  };

  // Rich search history item interface - type is inlined in state declaration
  // interface SearchHistoryItem {
  //   query: string;
  //   facilityBreakdown: Record<string, number>;
  //   totalCount: number;
  //   timestamp: number;
  // }

  // Compute facility type breakdown from search results
  const computeFacilityBreakdown = (wells: Well[]): Record<string, number> => {
    const breakdown: Record<string, number> = {};
    wells.forEach(well => {
      const type = well.type || 'well';
      breakdown[type] = (breakdown[type] || 0) + 1;
    });
    return breakdown;
  };

  // Calculate CSS gradient from facility breakdown
  const calculateGradient = (breakdown: Record<string, number>, fallbackColor: string): string => {
    const entries = Object.entries(breakdown);
    if (entries.length === 0) {
      return fallbackColor; // Solid fallback color
    }
    
    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    if (total === 0) {
      return fallbackColor;
    }
    
    // Sort by count descending for visual appeal
    entries.sort((a, b) => b[1] - a[1]);
    
    // Build gradient stops
    const stops: string[] = [];
    let currentPercent = 0;
    
    entries.forEach(([type, count]) => {
      const percent = (count / total) * 100;
      const color = FACILITY_COLORS[type] || FACILITY_COLORS.unknown;
      stops.push(`${color} ${currentPercent}%`);
      currentPercent += percent;
      stops.push(`${color} ${currentPercent}%`);
    });
    
    return `linear-gradient(135deg, ${stops.join(', ')})`;
  };

  // ==================== End Gradient Search Chips ====================

  // Facility type mapping: Display label -> API type value
  // API accepts: 'wells', 'swds', 'rigs', 'facilities', 'all'
  // For specific facility types, we use 'facilities' + facilityTypes parameter
  const FACILITY_TYPE_MAP: Record<string, string> = {
    'All': 'all',
    'Wells': 'wells',
    'SWDs': 'swds',
    'Rigs': 'rigs',
    'Fracking Site': 'facilities',
    'Drilling Site': 'facilities',
    'Tank Battery': 'facilities',
    'Flare Stack': 'facilities',
    'Compressor': 'facilities',
    'Gas Plant': 'facilities',
    'Brine Well': 'facilities',
    'Refinery': 'facilities',
    'Treating Plant': 'facilities',
    'Well Head': 'facilities',
    'Gas Gathering': 'facilities',
    'Injection Plant': 'facilities',
    'Gas Pipeline': 'facilities',
    'Crude Pipeline': 'facilities',
    'Water Pipeline': 'facilities',
    'Gas Storage': 'facilities',
    'Below Grade Tank': 'facilities',
    'Metering Station': 'facilities',
    'Crude Pump': 'facilities',
    'Pits': 'facilities',
    'Waste Mgmt': 'facilities',
    'Recycling': 'facilities',
    'Remediate': 'facilities',
    'Abatement': 'facilities',
    'Service Co.': 'facilities',
    'Class I/II': 'facilities',
    'Porta Potty': 'facilities',
    'Taco Truck': 'facilities',
    // Texas-specific well types
    'Horizontal Wells': 'tx-horizontal',
    'Directional Wells': 'tx-directional',
    'Oil Wells': 'tx-oil',
    'Gas Wells': 'tx-gas',
    'Oil/Gas Wells': 'tx-oil-gas'
  };

  // Facility sub-type codes for NM OCD facilities
  const FACILITY_SUBTYPE_MAP: Record<string, string> = {
    'Well Pad': 'WP',
    'Well Head': 'WH',
    'Fracking Site': 'FRK',
    'Drilling Site': 'DRL',
    'Tank Battery': 'TB',
    'Flare Stack': 'FS',
    'Compressor': 'CS',
    'Gas Plant': 'GP',
    'Gas Gathering': 'GGS',
    'Refinery': 'RF',
    'Treating Plant': 'TP',
    'Injection Plant': 'INJ',
    'Gas Pipeline': 'PLG',
    'Crude Pipeline': 'PLC',
    'Water Pipeline': 'PLW',
    'Gas Storage': 'GS',
    'Below Grade Tank': 'BGT',
    'Metering Station': 'MGS',
    'Crude Pump': 'CP',
    'Pits': 'PIT',
    'Waste Mgmt': 'WM',
    'Recycling': 'RFL',
    'Brine Well': 'BW',
    'Remediate': 'RM',
    'Abatement': 'AB',
    'Service Co.': 'SC',
    'Class I/II': 'CI',
    'Porta Potty': 'PP',
    'Taco Truck': 'TACO',
    // Texas well type codes
    'Horizontal Wells': 'TX_HORIZONTAL',
    'Directional Wells': 'TX_DIRECTIONAL',
    'Oil Wells': 'TX_OIL',
    'Gas Wells': 'TX_GAS',
    'Oil/Gas Wells': 'TX_OILGAS'
  };

  // Translations
  const { t } = useTranslation();
  
  // Get theme-aware cyberpunk colors
  const highContrast = useAppStore((s) => s.highContrast);
  const toggleHighContrast = useAppStore((s) => s.toggleHighContrast);
  const cyber = getCyberColors(darkMode, highContrast);

  // Load subscription status on mount
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        const status = await subscriptionService.getStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Failed to load subscription status:', error);
      }
    };
    loadSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (showSearchMode) {
      setFacilityRegion(null);
    }
  }, [showSearchMode]);

  // Listen for openSubscription event from ExpiredTrialBanner/feature gates
  useEffect(() => {
    const handleOpenSubscription = () => {
      setShowSubscription(true);
    };
    window.addEventListener('openSubscription', handleOpenSubscription);
    return () => {
      window.removeEventListener('openSubscription', handleOpenSubscription);
    };
  }, []);

  // Subscribe to road hazards - real-time updates
  useEffect(() => {
    // Start polling for hazards
    roadHazardsService.startPolling(30000); // Every 30 seconds
    
    // Subscribe to updates
    const unsubscribe = roadHazardsService.subscribe((hazards, newHazards) => {
      setRoadHazards(hazards);
      
      // Show notification for new hazards from other users
      if (newHazards.length > 0) {
        newHazards.forEach(hazard => {
          const info = roadHazardsService.constructor.prototype.constructor['getHazardInfo']?.(hazard.type) 
            || { emoji: '⚠️', label: hazard.type };
          console.log(`[Hazard] New ${info.label} reported nearby!`);
          // Haptic feedback for new hazards
          telegram.hapticFeedback('warning');
        });
      }
    });
    
    return () => {
      unsubscribe();
      roadHazardsService.stopPolling();
    };
  }, []);

  // Sync speech service with voice feedback setting
  useEffect(() => {
    speechService.setEnabled(voiceFeedbackEnabled);
  }, [voiceFeedbackEnabled]);

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(offlineService.getRecentSearches(RECENT_SEARCH_LIMIT));
  }, []);

  // Load rich search history for gradient chips on mount (fresh start, ignoring old recentSearches)
  useEffect(() => {
    const loadedHistory = offlineService.getRichSearchHistory();
    setSearchHistory(loadedHistory);
  }, []);

  /* Temporarily disabled with Basin FABs
  // Allow external injection of channel unread counts via window.__basinChannelUnread and optional polling API
  useEffect(() => {
    const injected = (window as any).__basinChannelUnread;
    if (injected && typeof injected === 'object') {
      setChannelBadges((prev) => ({ ...prev, ...injected }));
    }

    let timer: number | undefined;

    const fetchUnread = async () => {
      try {
        const res = await fetch(channelUnreadUrl);
        if (!res.ok) return;
        const data = await res.json();
        if (data && typeof data === 'object') {
          setChannelBadges((prev) => ({ ...prev, ...data }));
        }
      } catch (e) {
        // silent; will retry on next interval
      }
    };

    fetchUnread();
    timer = window.setInterval(fetchUnread, 30000);

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [channelUnreadUrl]);
  */

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (voiceFeedbackEnabled) {
        speechService.announceOnlineMode();
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      if (voiceFeedbackEnabled) {
        speechService.announceOfflineMode();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial status
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setIsOffline, voiceFeedbackEnabled]);

  // Initialize cache service and check for onboarding
  useEffect(() => {
    const initializeCache = async () => {
      await cacheService.initialize();
      
      // Always reset integrityVerified on mount to reflect actual cache state
      setIntegrityVerified(false);
      
      // Check if user has seen onboarding
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      if (!hasSeenOnboarding) {
        setShowOnboarding(true);
      }
      
      // Load cache info
      const syncInfo = await cacheService.getLastSyncInfo();
      const cacheSize = await cacheService.getTotalCacheSize();
      setCacheInfo({
        lastSync: syncInfo?.timestamp ? new Date(syncInfo.timestamp) : undefined,
        size: cacheSize.total
      });
      
      // Check for pending update notification
      const pendingUpdate = localStorage.getItem('pendingUpdateNotification');
      if (pendingUpdate) {
        const updateInfo = JSON.parse(pendingUpdate);
        telegram.showAlert(`✅ Data Updated: Facility data was updated overnight (${new Date(updateInfo.timestamp).toLocaleString()})`);
        localStorage.removeItem('pendingUpdateNotification');
      }
      
      // Initialize Service Worker for midnight sync
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          
          // Listen for cache update messages from Service Worker
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'CACHE_UPDATED') {
              console.log('📬 Received cache update notification from SW:', event.data);
              telegram.showAlert(`✅ Data updated overnight: ${event.data.successCount} cached successfully`);
              
              // Update cache info
              cacheService.getLastSyncInfo().then(syncInfo => {
                cacheService.getTotalCacheSize().then(size => {
                  setCacheInfo({
                    lastSync: syncInfo?.timestamp ? new Date(syncInfo.timestamp) : undefined,
                    size: size.total
                  });
                });
              });
            }
          });
          
          // Start the midnight sync scheduler
          if (registration.active) {
            registration.active.postMessage({ type: 'START_MIDNIGHT_SCHEDULER' });
            console.log('⏰ Midnight sync scheduler started');
          }
        } catch (error) {
          console.error('❌ Service Worker initialization error:', error);
        }
      }
    };
    
    initializeCache();
  }, []);

  // Preload full wells cache on mount (always refresh to pick up latest + version changes)
  useEffect(() => {
    const preloadData = async () => {
      if (navigator.onLine) {
        console.log('⬇️ Preloading full wells cache for offline mode...');
        await offlineService.preloadTopWells();
      } else {
        console.log('⚠️ Skipping preload (offline)');
      }
    };
    preloadData();
  }, []);

  // Get and update current location for crews feature
  useEffect(() => {
    const updateLocation = async () => {
      try {
        const location = await NavigationService.getCurrentLocation();
        if (location) {
          setCurrentLocation({ lat: location.lat, lng: location.lng });
        }
      } catch (e) {
        console.log('Could not get current location for crews:', e);
      }
    };
    
    // Get initial location
    updateLocation();
    
    // Update location every 30 seconds when crews panel is open
    const intervalId = setInterval(() => {
      if (showCrews) {
        updateLocation();
      }
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, [showCrews]);

  // Check which basins have been downloaded
  useEffect(() => {
    const checkDownloadedBasins = async () => {
      const basinCounties: Record<string, string[]> = {
        delaware: ['Eddy', 'Lea'],
        permian: ['Lea', 'Eddy', 'Chaves', 'Roosevelt'],
        'san-juan': ['San Juan', 'Rio Arriba', 'Sandoval', 'McKinley'],
        raton: ['Colfax', 'Union', 'Taos']
      };

      const facilityTypes: ('wells' | 'swds' | 'rigs' | 'facilities')[] = ['wells', 'swds', 'rigs', 'facilities'];
      const downloadStatus: Record<string, boolean> = {};
      
      for (const [basin, counties] of Object.entries(basinCounties)) {
        try {
          // Check if at least one county from the basin has cached facilities
          let hasData = false;
          for (const county of counties) {
            for (const facilityType of facilityTypes) {
              const cachedWells = await cacheService.getCachedData(county, facilityType);
              if (cachedWells && cachedWells.length > 0) {
                hasData = true;
                break;
              }
            }
            if (hasData) break;
          }
          downloadStatus[basin] = hasData;
        } catch (error) {
          console.warn(`Error checking basin ${basin}:`, error);
          downloadStatus[basin] = false;
        }
      }
      
      setDownloadedBasins(downloadStatus as typeof downloadedBasins);
    };

    checkDownloadedBasins();
  }, []);

  // Handle nearby location from bot
  useEffect(() => {
    if (nearbyLocation) {
      // Automatically search for nearby wells
      const searchNearby = async () => {
        setSearchLoading(true);
        setShowSearchResults(true);
        try {
          const response = await fetch(
            `/api/facilities/search?lat=${nearbyLocation.lat}&lng=${nearbyLocation.lng}&radius=25&type=all`
          );
          const data = await response.json();
          
          if (data.success && data.facilities) {
            setSearchResults(data.facilities);
            setSearchQuery('Nearby Wells');
            if (voiceFeedbackEnabled) {
              speechService.announceSearchResults(data.facilities.length, 'nearby wells');
            }
          }
        } catch (error) {
          console.error('Nearby search failed:', error);
        } finally {
          setSearchLoading(false);
          // Clear the nearby location after using it
          setNearbyLocation(null);
        }
      };
      searchNearby();
    }
  }, [nearbyLocation, voiceFeedbackEnabled, setNearbyLocation]);

  // Open Photo Routes if there's a shared route photo
  useEffect(() => {
    if (sharedRoutePhotoId) {
      setShowCommunityRoutes(true);
    }
  }, [sharedRoutePhotoId]);

  // Open Map if there's a shared facility
  useEffect(() => {
    if (sharedFacilityId) {
      // Fetch the shared facility and open map centered on it
      const fetchSharedFacility = async () => {
        try {
          const response = await fetch(`/api/submissions/approved?id=${sharedFacilityId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.facility) {
              // Create a Well-like object from the facility
              const facilityAsWell: Well = {
                id: data.facility.id,
                api: data.facility.apiNumber || '',
                wellName: data.facility.name,
                operator: data.facility.operator || 'Community Submitted',
                latitude: data.facility.latitude,
                longitude: data.facility.longitude,
                county: data.facility.county || '',
                status: 'Active',
                basin: '',
                type: data.facility.type,
              };
              setSelectedWell(facilityAsWell);
              setShowFavoritesMap(true);
            }
          }
        } catch (error) {
          console.error('Failed to fetch shared facility:', error);
        } finally {
          // Clear the shared facility ID
          setSharedFacilityId(null);
        }
      };
      fetchSharedFacility();
    }
  }, [sharedFacilityId, setSharedFacilityId]);

  // Sync editable query with transcript
  useEffect(() => {
    if (transcript) {
      setEditableQuery(transcript);
    }
  }, [transcript]);

  // Auto-search with debounce when user pauses typing
  useEffect(() => {
    // Clear no results when query changes
    setNoResultsQuery(null);
    
    // Don't search if empty, already searching, or listening
    const query = editableQuery.trim();
    if (!query || searchLoading || status === 'listening' || status === 'processing') {
      return;
    }
    
    // Debounce: wait 600ms after user stops typing
    const debounceTimer = setTimeout(() => {
      console.log('🔍 [AUTO-SEARCH] Triggering search for:', query);
      handleManualSearch();
    }, 600);
    
    return () => clearTimeout(debounceTimer);
  }, [editableQuery]);

  // Load favorites from Telegram CloudStorage on mount and sync with appStore
  useEffect(() => {
    loadFavorites();
  }, []);
  
  useEffect(() => {
    // Detect if we're in Telegram Mini App WebView
    const telegramUser = telegram.getUser();
    const hasTelegramWebApp = !!(window as any).Telegram?.WebApp?.initData;
    const inTelegram = !!telegramUser || hasTelegramWebApp;
    setIsInTelegramApp(inTelegram);
    
    // Detect Android
    const isAndroid = /android/i.test(navigator.userAgent);
    
    // Check browser Web Speech API availability
    const browserSpeechAvailable = !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition;
    
    console.log('🔍 Voice API Detection:');
    console.log('  - In Telegram Mini App:', inTelegram);
    console.log('  - Platform:', isAndroid ? 'Android' : 'iOS/Other');
    console.log('  - Telegram WebApp version:', telegram.webApp?.version || 'N/A');
    console.log('  - Browser SpeechRecognition available:', browserSpeechAvailable);
    
    // Android Telegram WebView typically doesn't support Web Speech API
    if (inTelegram && isAndroid) {
      console.warn('⚠️ Android Telegram WebView - voice recognition likely not supported');
      setVoiceAvailable(false);
      // Don't show error immediately - user can still try
    }
    
    // Always try Web Speech API - it works in browsers and iOS Telegram
    if (browserSpeechAvailable) {
      console.log('✅ Web Speech API detected, attempting to use it');
      setVoiceAvailable(true);
      useTelegramVoice.current = false;
      
      const service = new VoiceService();
      voiceService.current = service;

      // Check if voice recognition is supported
      if (!service.isSupported()) {
        console.error('❌ Voice recognition not supported');
        setVoiceAvailable(false);
        setErrorMessage(t('voiceNotSupportedDevice'));
        setStatus('error');
      } else {
        console.log('✅ Voice service initialized successfully');
        if (inTelegram && isAndroid) {
          console.log('⚠️ Note: Voice may not work on Android Telegram WebView');
        }
      }

      // Set up event handlers
      service.onStart = () => {
        setStatus('listening');
        setTranscript('');
        setErrorMessage('');
        
        // Voice feedback when listening starts
        if (voiceFeedbackEnabled) {
          speechService.announceListening();
        }
      };

      service.onEnd = () => {
        console.log('🎤 Voice recognition ended, current status:', status);
        setStatus('idle');
      };

      // Handle interim results for live display
      (service as VoiceService).onInterimResult = (interimTranscript: string) => {
        console.log('📝 Interim transcript:', interimTranscript);
        setTranscript(interimTranscript);
      };

      service.onResult = (result: any) => {
        const transcript = typeof result === 'string' ? result : result.transcript;
        console.log('📝 onResult received transcript:', transcript);
        setTranscript(transcript);
        setStatus('processing');
        
        // Process voice command
        console.log('⏳ Scheduling processVoiceCommand for:', transcript);
        setTimeout(() => {
          processVoiceCommand(transcript);
        }, 500);
      };

      service.onError = (error) => {
        // Detect platform for appropriate error message
        const isAndroid = /android/i.test(navigator.userAgent);
        
        // In Telegram, provide more helpful error message
        let errorMsg = error;
        if (inTelegram && (error.includes('not-allowed') || error.includes('denied') || error.includes('not supported'))) {
          if (isAndroid) {
            errorMsg = '🤖 Voice not supported on Android Telegram. Please type your search or tap facility buttons below.';
          } else {
            errorMsg = 'Voice not available. Use the search bar below.';
          }
        }
        setErrorMessage(errorMsg);
        setStatus('error');
        
        // Reset to idle after showing error
        setTimeout(() => {
          if (status === 'error') {
            setStatus('idle');
            setErrorMessage('');
          }
        }, 3000);
      };

      // Set up silence detection callback
      (service as VoiceService).onSilenceDetected = () => {
        console.log('🔇 Silence detected, auto-stopping...');
        // Status will transition from listening → processing via onResult callback
      };

      return () => {
        service.cleanup();
      };
    } else {
      // No Web Speech API available at all
      console.warn('⚠️ No Web Speech API available');
      setVoiceAvailable(false);
      if (inTelegram) {
        console.log('ℹ️ Voice not available in this Telegram WebView');
      }
    }
  }, []);

  // Load favorites from Telegram CloudStorage and sync with appStore
  const loadFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const savedFavorites = await telegram.getFavorites();
      // Sync telegram favorites with appStore
      savedFavorites.forEach((well: Well) => {
        if (!isFavorite(well.id)) {
          addFavoriteToStore(well);
        }
      });
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // Add well to favorites (both telegram and appStore)
  const addToFavorites = async (well: Well) => {
    try {
      await telegram.saveFavorite(well.id, well);
      addFavoriteToStore(well);
      telegram.hapticFeedback('success');
    } catch (error) {
      console.error('Failed to save favorite:', error);
      telegram.hapticFeedback('error');
    }
  };

  // Remove well from favorites (both telegram and appStore)
  const removeFromFavorites = async (well: Well) => {
    try {
      await telegram.removeFavorite(well.id);
      removeFavoriteFromStore(well.id);
      telegram.hapticFeedback('success');
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      telegram.hapticFeedback('error');
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (well: Well) => {
    if (isFavorite(well.id)) {
      await removeFromFavorites(well);
    } else {
      await addToFavorites(well);
    }
  };

  const processVoiceCommand = async (transcriptText: string) => {
    console.log('🎤 [VOICE COMMAND] Processing:', transcriptText, '| Status:', status);
    const command = parseVoiceCommand(transcriptText);
    console.log('🎤 [VOICE COMMAND] Parsed:', command);

    if (!command) {
      setErrorMessage(t('commandNotRecognized'));
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
      return;
    }

    try {
      switch (command.action) {
        case 'navigate':
          console.log('🎤 [VOICE COMMAND] Action: Navigate to', command.params[0]);
          await handleNavigateCommand(command.params[0]);
          break;
        
        case 'nearest':
          console.log('🎤 [VOICE COMMAND] Action: Find nearest');
          await handleNearestCommand();
          break;
        
        case 'search':
          console.log('🎤 [VOICE COMMAND] Action: Search for', command.params[0], '| Voice activated search');
          await handleSearchCommand(command.params[0]);
          break;
        
        case 'back':
        case 'cancel':
          console.log('🎤 [VOICE COMMAND] Action: Cancel');
          setStatus('idle');
          break;
        
        default:
          console.log('🎤 [VOICE COMMAND] Action: Unknown -', command.action);
          setErrorMessage(t('commandNotImplemented'));
          setStatus('error');
          setTimeout(() => setStatus('idle'), 2000);
      }
    } catch (error) {
      console.error('❌ [VOICE COMMAND] Error processing:', error);
      setErrorMessage(t('failedToProcessCommand'));
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
    }
  };

  const handleNavigateCommand = async (query: string) => {
    // Search for well by API or name
    const well = await searchWell(query);
    
    if (!well) {
      setErrorMessage(`Well "${query}" not found`);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
      return;
    }

    // Get route preview
    const currentLocation = await NavigationService.getCurrentLocation();
    let routeInfo = null;
    
    if (currentLocation) {
      routeInfo = await NavigationService.getRoutePreview(
        currentLocation,
        { lat: well.latitude, lng: well.longitude }
      );
    }

    // Show confirmation and navigate
    setStatus('success');
    setSelectedWell(well);
    
    // Voice feedback
    if (voiceFeedbackEnabled) {
      speechService.announceNavigation(well.wellName);
    }
    
    const confirmed = await NavigationService.confirmNavigation(well, routeInfo);
    
    if (confirmed) {
      NavigationService.openGoogleMaps(well);
    }
    
    setStatus('idle');
  };

  const handleNearestCommand = async () => {
    const currentLocation = await NavigationService.getCurrentLocation();
    
    if (!currentLocation) {
      setErrorMessage(t('locationAccessRequired'));
      setStatus('error');
      setTimeout(() => setStatus('idle'), 2000);
      return;
    }

    // Find nearest well (placeholder - will integrate with D1)
    telegram.webApp.showAlert(t('findingNearestWell'));
    setStatus('idle');
  };

  const handleSearchCommand = async (query: string) => {
    console.log('🔎 [TEXT SEARCH] Starting:', query, '| Status before:', status, '| From text input');
    setSearchQuery(query);
    setSearchLoading(true);
    // Don't open panel yet - wait for results to load for smoother reveal
    setStatus('processing');
    // Close Search mode dialog when navigating to results
    setShowSearchMode(false);
    
    console.log('🔎 [TEXT SEARCH] States set:', {
      searchQuery: query,
      searchLoading: true,
      showSearchResults: 'waiting for results',
      voiceStatus: 'processing'
    });
    
    try {
      let wells: Well[] = [];
      const trimmedQuery = query.trim();
      
      // Try offline cache first if offline
      if (isOffline) {
        console.log('📡 [TEXT SEARCH] Offline mode - searching cache');
        
        // Use cacheService.searchCachedWells which actually searches IndexedDB
        wells = await cacheService.searchCachedWells(query, 100);
        
        if (wells.length === 0) {
          // Try exact cache hit from localStorage (recent searches)
          const cachedResults = offlineService.getCachedSearch(query);
          if (cachedResults) {
            wells = cachedResults;
          }
        }
        
        if (wells.length === 0) {
          throw new Error('No cached results available offline. Download data first from the menu.');
        }
      } else {
        // Online - ALWAYS search ALL facility types from text search
        console.log('🌐 [TEXT SEARCH] Online - searching ALL facilities');
        const startTime = Date.now();
        
        let url = `/api/facilities/search?type=all`;
        
        // Check if query is API number
        if (/^\d{2}-\d{3}-\d{5}/.test(trimmedQuery)) {
          url += `&api=${encodeURIComponent(trimmedQuery)}`;
        } else if (trimmedQuery) {
          url += `&query=${encodeURIComponent(trimmedQuery)}`;
        }
        
        // Add location for distance sorting
        try {
          const location = await NavigationService.getCurrentLocation();
          if (location) {
            url += `&lat=${location.lat}&lng=${location.lng}`;
          }
        } catch (e) {
          console.log('⚠️ Location not available');
        }
        
        console.log('📡 [TEXT SEARCH] Fetching:', url);
        const response = await fetch(url);
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API returned ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        const fetchTime = Date.now() - startTime;
        
        if (data.success && data.facilities) {
          wells = data.facilities.map(mapFacilityToWell);
          setSearchCacheInfo({ count: wells.length, time: fetchTime, cached: false });
          console.log('✅ [TEXT SEARCH] Found', wells.length, 'facilities in', fetchTime, 'ms');
        } else {
          setSearchCacheInfo({ count: 0, time: fetchTime, cached: false });
        }
      }

      // Cache results for offline use and refresh recents (online or offline) for any non-empty query
      if (trimmedQuery) {
        offlineService.cacheSearch(trimmedQuery, wells);
        setRecentSearches(offlineService.getRecentSearches(RECENT_SEARCH_LIMIT));
        
        // Save rich search history for gradient chips
        const facilityBreakdown = computeFacilityBreakdown(wells);
        const historyItem = {
          query: trimmedQuery,
          facilityBreakdown,
          totalCount: wells.length,
          timestamp: Date.now()
        };
        offlineService.saveRichSearchHistory(historyItem);
        setSearchHistory(offlineService.getRichSearchHistory());
        
        console.log('💾 [TEXT SEARCH] Cached results and refreshed recents + rich history');
      }
      
      console.log('✅ [TEXT SEARCH] Results:', wells.length, 'facilities found');
      setSearchResults(wells);
      setSearchLoading(false); // Clear loading before opening panel
      setShowSearchResults(true); // NOW open panel - results are ready!
      setStatus('success');
      
      // Voice feedback
      if (voiceFeedbackEnabled) {
        speechService.announceSearchResults(wells.length, query);
      }
      
      // Keep success status for 1 second, then reset to idle but keep dialog open
      setTimeout(() => {
        setStatus('idle');
      }, 1000);
    } catch (error) {
      console.error('❌ Search error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setErrorMessage(`Search failed: ${errorMsg}`);
      setStatus('error');
      setSearchResults([]);
      setShowSearchResults(false); // Close dialog on error
      
      // Voice feedback for error
      if (voiceFeedbackEnabled) {
        speechService.announceError(`Could not find ${query}`);
      }
      
      // Show user-friendly message
      setTimeout(() => {
        telegram.showAlert(`Could not search for "${query}". ${isOffline ? 'No cached results available.' : 'Please try again.'}`);
      }, 500);
      
      // Reset error status after showing
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 3000);
    } finally {
      setSearchLoading(false);
    }
  };

  const searchWell = async (query: string): Promise<Well | null> => {
    try {
      // Build search URL based on query type
      let url = '/api/facilities/search?type=all';
      
      // Check if query is API number (format: 30-XXX-XXXXX)
      if (/^\d{2}-\d{3}-\d{5}/.test(query)) {
        url += `&api=${encodeURIComponent(query)}`;
      } else {
        url += `&query=${encodeURIComponent(query)}`;
      }
      
      // Add user location for distance calculation
      try {
        const location = await NavigationService.getCurrentLocation();
        if (location) {
          url += `&lat=${location.lat}&lng=${location.lng}`;
        }
      } catch (e) {
        console.log('Location not available, searching without distance');
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success && data.wells && data.wells.length > 0) {
        // Return closest well if multiple results
        const wellData = data.wells[0];
        return {
          id: wellData.id,
          api: wellData.apiNumber,
          wellName: wellData.wellName,
          latitude: wellData.latitude,
          longitude: wellData.longitude,
          county: wellData.county,
          operator: wellData.operator || 'Unknown',
          status: wellData.status || 'Unknown',
          basin: getBasinForCounty(wellData.county),
          distance: wellData.distance
        } as Well;
      }
      
      return null;
    } catch (error) {
      console.error('Well search error:', error);
      telegram.webApp.showAlert(t('searchFailedRetry'));
      return null;
    }
  };

  const handleVoiceButtonPress = async () => {
    console.log('🎤 [VOICE BUTTON] Pressed | Status:', status, '| Voice interface activated');
    telegram.hapticFeedback('light');
    
    // Check premium feature access for voice navigation
    const hasAccess = await featureAccess.checkAccessWithPrompt('voiceNav');
    if (!hasAccess) {
      console.log('🔒 [VOICE BUTTON] Voice nav locked - trial expired');
      return;
    }
    
    // Set a new random color for the active fingerprint each time
    setActiveFingerprintColor(vibrantColors[Math.floor(Math.random() * vibrantColors.length)]);
    
    // Detect Android
    const isAndroid = /android/i.test(navigator.userAgent);
    
    // If voice service isn't available, show error
    if (!voiceService.current) {
      let msg: string;
      if (isInTelegramApp && isAndroid) {
        msg = '🤖 Voice not supported on Android Telegram. Tap the facility type buttons below or use the search bar to type your search!';
      } else if (isInTelegramApp) {
        msg = 'Voice not supported in this app. Use search bar below.';
      } else {
        msg = 'Voice recognition not available on this device.';
      }
      
      // Show alert for Android users
      if (isAndroid && isInTelegramApp) {
        telegram.showAlert(t('voiceSearchNotSupported'));
      }
      
      setErrorMessage(msg);
      setStatus('error');
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 4000);
      return;
    }
    
    // If in success/error state, reset to idle first
    if (status === 'success' || status === 'error') {
      console.log('🔄 [VOICE BUTTON] Resetting from', status, 'to idle');
      setStatus('idle');
      setErrorMessage('');
      return;
    }
    
    // Auto-stop mode: Button only starts recording
    // Silence detection will auto-stop after 1.5s of silence
    if (status === 'idle') {
      // Start listening
      console.log('▶️ [VOICE BUTTON] Starting voice recognition (auto-stop after silence)');
      const started = voiceService.current?.start();
      console.log('▶️ [VOICE BUTTON] Voice service started:', started, '| Status will change to "listening"');
    } else if (status === 'listening') {
      // Optional: Allow manual stop if user taps again during listening
      console.log('🛑 [VOICE BUTTON] Manual stop requested');
      voiceService.current?.stop();
    } else {
      console.log('⚠️ [VOICE BUTTON] Cannot start, status is:', status);
    }
  };

  const handleSpinnerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleVoiceButtonPress();
    }
  };

  // Handle text selection for voice editing

  // Toggle sort order
  const handleToggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Sort search results
  const sortedSearchResults = [...searchResults].sort((a, b) => {
    const nameA = a.wellName.toUpperCase();
    const nameB = b.wellName.toUpperCase();
    if (sortOrder === 'asc') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  // Handle manual search from edited query - ALWAYS searches ALL facility types
  // Uses pagination for large result sets
  const SEARCH_PAGE_SIZE = 5000; // Load 5000 at a time for performance
  
  const handleManualSearch = async (isLoadMore = false) => {
    // Allow empty search to show all facilities
    if (!isLoadMore) {
      setSearchLoading(true);
      setStatus('processing');
      setSearchOffset(0);
      setSearchResults([]);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const query = editableQuery.trim();
      const currentOffset = isLoadMore ? searchOffset : 0;
      const startTime = Date.now();
      
      // Store query for pagination and clear facility type mode (manual search overrides)
      if (!isLoadMore) {
        searchQueryRef.current = query;
        facilityTypeLoadModeRef.current = null; // Clear facility type mode for manual search
      }
      
      // Build search URL - ALWAYS search ALL facility types from the search bar
      let url = `/api/facilities/search?type=all&limit=${SEARCH_PAGE_SIZE}&offset=${currentOffset}`;
      
      // Check if query is API number
      if (/^\d{2}-\d{3}-\d{5}/.test(query)) {
        url += `&api=${encodeURIComponent(query)}`;
      } else if (query) {
        url += `&query=${encodeURIComponent(query)}`;
      } else {
        // Empty query = fetch all facilities with pagination
        url += `&fetchAll=true`;
      }
      
      // Add location for distance sorting
      try {
        const location = await NavigationService.getCurrentLocation();
        if (location) {
          url += `&lat=${location.lat}&lng=${location.lng}`;
        }
      } catch (e) {
        console.log('⚠️ Location not available for search');
      }
      
      console.log(`🔍 [SEARCH BAR] ${isLoadMore ? 'Loading more' : 'Searching'} ALL facilities:`, url);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API returned ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      const fetchTime = Date.now() - startTime;
      
      if (data.success && data.facilities && data.facilities.length > 0) {
        const newResults = data.facilities.map(mapFacilityToWell);
        const totalCount = data.total || data.count || newResults.length;
        
        console.log(`✅ [SEARCH BAR] ${isLoadMore ? 'Loaded more' : 'Found'} ${newResults.length} facilities (total: ${totalCount}) in ${fetchTime}ms`);
        
        // Append or replace results
        if (isLoadMore) {
          setSearchResults(prev => [...prev, ...newResults]);
        } else {
          setSearchResults(newResults);
          // Set fetch info for inline display
          setSearchCacheInfo({ count: newResults.length, time: fetchTime, cached: false });
          
          // Cache and refresh recents for any non-empty manual query
          if (query) {
            offlineService.cacheSearch(query, newResults);
            setRecentSearches(offlineService.getRecentSearches(RECENT_SEARCH_LIMIT));
            
            // Save rich search history for gradient chips
            const facilityBreakdown = computeFacilityBreakdown(newResults);
            const historyItem = {
              query,
              facilityBreakdown,
              totalCount: newResults.length,
              timestamp: Date.now()
            };
            offlineService.saveRichSearchHistory(historyItem);
            setSearchHistory(offlineService.getRichSearchHistory());
          }
        }
        
        // Update pagination state
        const newOffset = currentOffset + newResults.length;
        setSearchOffset(newOffset);
        setSearchTotalCount(totalCount);
        setSearchHasMore(newOffset < totalCount);
        
        setNoResultsQuery(null);
        setSearchQuery(query || 'All Facilities');
        setShowSearchResults(true);
        // Close Search mode dialog when navigating to results
        setShowSearchMode(false);
        if (!isLoadMore) {
          setStatus('success');
        }
      } else if (!isLoadMore) {
        // No results on initial search
        setSearchCacheInfo({ count: 0, time: fetchTime, cached: false });
        setSearchResults([]);
        setSearchTotalCount(0);
        setSearchHasMore(false);
        setNoResultsQuery(query || null);
        setShowSearchResults(false);
        setSearchQuery(query || 'All Facilities');
        setStatus('idle');
      }
    } catch (error) {
      console.error('Manual search error:', error);
      if (!isLoadMore) {
        setErrorMessage(t('searchFailedRetry'));
        setStatus('error');
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setSearchLoading(false);
      }
    }
  };
  
  // Load more "all" facilities for infinite scroll
  const loadMoreAllFacilities = useCallback(async () => {
    if (loadingMore || !searchHasMore) return;
    
    setLoadingMore(true);
    const ALL_FACILITIES_PAGE_SIZE = 5000;
    const currentOffset = searchOffset;
    
    try {
      let url = `/api/facilities/search?type=all&limit=${ALL_FACILITIES_PAGE_SIZE}&offset=${currentOffset}`;
      if (selectedCounty) {
        url += `&county=${encodeURIComponent(selectedCounty)}`;
      }
      
      console.log(`🔄 [LOAD MORE] Fetching offset ${currentOffset}...`);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (data.success && data.facilities?.length > 0) {
        const newResults = data.facilities.map(mapFacilityToWell);
        const newOffset = currentOffset + newResults.length;
        const totalCount = data.total || searchTotalCount;
        
        setSearchResults(prev => [...prev, ...newResults]);
        setSearchOffset(newOffset);
        setSearchHasMore(newOffset < totalCount);
        
        console.log(`✅ [LOAD MORE] Loaded ${newResults.length} more (total: ${newOffset}/${totalCount})`);
      } else {
        setSearchHasMore(false);
      }
    } catch (error) {
      console.error('❌ [LOAD MORE] Error:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, searchHasMore, searchOffset, searchTotalCount, selectedCounty]);
  
  // Load more results callback for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && searchHasMore) {
      // Check if we're in facility type "all" mode
      if (facilityTypeLoadModeRef.current === 'all') {
        loadMoreAllFacilities();
      } else {
        handleManualSearch(true);
      }
    }
  }, [loadingMore, searchHasMore, loadMoreAllFacilities]);

  // COMMENTED: handleDeleteRecentSearch removed with recent searches from Facilities dialog
  // const handleDeleteRecentSearch = (query: string) => {
  //   telegram.hapticFeedback('light');
  //   offlineService.removeRecentSearch(query);
  //   setRecentSearches(offlineService.getRecentSearches(RECENT_SEARCH_LIMIT));
  // };

  /* Temporarily disabled with Basin FABs
  const openBasinChannel = (link: string) => {
    if (telegram && typeof telegram.openTelegramLink === 'function') {
      telegram.openTelegramLink(link);
    } else {
      window.open(link, '_blank');
    }
  };
  */

  // ==================== REMOVED: getStatusIcon, getStatusText, getHelpText ====================
  // These functions were part of the old mic/input UI that's been replaced by the canvas
  // with CyberSpinner and gradient search chips. They are no longer needed.
  // See git history for the original implementations if restoration is needed.

  const handleBasinSelect = (basin: Basin) => {
    setShowBasinDialog(false);
    NavigationService.downloadOfflineMap(basin);
  };

  const handleWellSelect = (well: Well) => {
    setShowSearchResults(false);
    setSelectedWell(well);
  };

  // Handle viewing a well on the map - opens FavoritesMapView with well selected
  const handleViewOnMap = (well: Well) => {
    // Ensure the well has a valid id - generate one if missing
    const wellWithId: Well = well.id ? well : {
      ...well,
      id: `temp-${well.api || ''}-${well.latitude}-${well.longitude}-${Date.now()}`
    };
    
    // Add to favorites temporarily if not already saved (so it shows on map)
    if (!isFavorite(wellWithId.id)) {
      addFavoriteToStore(wellWithId);
    }
    setShowSearchResults(false);
    setSelectedWell(wellWithId);
    setShowFavoritesMap(true);
  };

  const handleCloseSearchResults = () => {
    setShowSearchResults(false);
    setSearchResults([]);
    setSearchQuery('');
    setSearchCacheInfo(null);
    setStatus('idle'); // Reset status to allow new searches
  };

  const withAlpha = (hexColor: string, alpha: number): string => {
    const clean = hexColor.replace('#', '');
    const normalized = clean.length === 3
      ? clean.split('').map((char) => char + char).join('')
      : clean;
    const base = normalized.slice(0, 6);
    const intval = parseInt(base, 16);
    if (Number.isNaN(intval)) return hexColor;
    const r = (intval >> 16) & 255;
    const g = (intval >> 8) & 255;
    const b = intval & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleFacilityRegionSelect = (region: 'NM' | 'TX') => {
    telegram.hapticFeedback('light');
    setFacilityRegion(region);
  };

  const filterByFacilityRegion = useCallback((items: Well[]): Well[] => {
    if (!facilityRegion) return items;
    const regionCode = facilityRegion.toUpperCase();
    return items.filter((item) => ((item.state || 'NM').toUpperCase()) === regionCode);
  }, [facilityRegion]);

  const facilityRegionLabel = facilityRegion === 'TX' ? 'Texas' : facilityRegion === 'NM' ? 'New Mexico' : '';

  const facilityRegionOptions = useMemo(() => ([
    {
      label: 'New Mexico',
      code: 'NM' as const,
      accent: '#0891b2',
      blurb: 'Delaware, San Juan, Raton basins',
      badge: {
        icon: WbSunnyIcon,
        iconColor: '#fefce8',
        gradient: ['#0ea5e9', '#1d4ed8'] as const,
        beam: '#22d3ee',
        ring: '#38bdf8',
        tagline: 'Solar arrays with desert telemetry and high plains routing.',
        signal: 'Southwest Solar Node',
        highlights: ['High desert ops', 'San Juan lift', 'Raton CO2 lanes'],
      },
    },
    {
      label: 'Texas',
      code: 'TX' as const,
      accent: '#ea580c',
      blurb: 'Permian (TX) & Eagle Ford plays',
      badge: {
        icon: ElectricBoltIcon,
        iconColor: '#ffe4e6',
        gradient: ['#fb7185', '#f97316'] as const,
        beam: '#fb7185',
        ring: '#f97316',
        tagline: 'Fracturing super-grid with nonstop refinery bandwidth.',
        signal: 'Permian Dynamo Hub',
        highlights: ['Mega pads', 'Eagle Ford swing', 'West TX lift'],
      },
    },
  ]), []);

  const renderRegionBadge = (region: typeof facilityRegionOptions[number]) => {
    const { badge } = region;
    const { icon: Icon, gradient, beam, ring, iconColor, tagline, signal, highlights } = badge;

    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          padding: { xs: 2.5, sm: 3 },
          background: `linear-gradient(135deg, ${withAlpha(gradient[0], 0.25)} 0%, ${withAlpha(gradient[1], 0.7)} 100%)`,
          border: `1px solid ${withAlpha(ring, 0.65)}`,
          boxShadow: `0 22px 48px ${withAlpha(ring, 0.3)}`,
          isolation: 'isolate',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(circle at 15% 20%, ${withAlpha(beam, 0.55)}, transparent 55%)`,
            mixBlendMode: 'screen',
            opacity: 0.85,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: '-40% -15% auto',
            height: '70%',
            pointerEvents: 'none',
            background: `linear-gradient(120deg, ${withAlpha(ring, 0.55)} 0%, transparent 75%)`,
            transform: 'rotate(8deg)',
            mixBlendMode: 'overlay',
          }}
        />
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              width: { xs: 58, sm: 64 },
              height: { xs: 58, sm: 64 },
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${withAlpha(ring, 0.8)}`,
              background: `radial-gradient(circle at 50% 40%, ${withAlpha(ring, 0.55)}, ${withAlpha(ring, 0.15)})`,
              boxShadow: `0 0 28px ${withAlpha(ring, 0.55)}`,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 4,
                borderRadius: '50%',
                border: `1px dashed ${withAlpha(ring, 0.4)}`,
                opacity: 0.8,
              },
            }}
          >
            <Icon sx={{ fontSize: { xs: 30, sm: 34 }, color: iconColor, filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.7))' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, letterSpacing: '0.14em', textTransform: 'uppercase', color: withAlpha(iconColor, 0.95) }}>
              {signal}
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.4, color: withAlpha(iconColor, 0.9) }}>
              {tagline}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 1.2, mt: { xs: 2, sm: 2.4 } }}>
          {highlights.map((highlight) => (
            <Box
              key={highlight}
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                backgroundColor: withAlpha(ring, 0.25),
                border: `1px solid ${withAlpha(ring, 0.55)}`,
                color: withAlpha(iconColor, 0.95),
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {highlight}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Handle facility type chip click
  const handleFacilityTypeClick = async (label: string) => {
    console.log('🎯 [CHIP CLICK] Starting:', label, '| Status before:', status, '| Voice NOT activated');
    telegram.hapticFeedback('light');
    
    if (!facilityRegion) {
      telegram.showAlert('Choose New Mexico or Texas first.');
      return;
    }

    // Map label to API type
    const apiType = FACILITY_TYPE_MAP[label] || 'all';
    const regionParam = facilityRegion;
    setSelectedFacilityType(apiType);
    setSelectedFacilityLabel(label);
    
    // Directly trigger search for this facility type without activating voice interface
    setSearchQuery(label);
    setSearchLoading(true);
    setIsFacilityTypeLoading(true); // Show full-screen CyberSpinner
    // Don't show SearchResults yet - wait for data to load
    
    console.log('🎯 [CHIP CLICK] States set:', {
      apiType,
      searchQuery: label,
      searchLoading: true,
      isFacilityTypeLoading: true,
      voiceStatus: status, // Should still be idle/previous state
      region: regionParam
    });
    
    // Track facility type mode for Load More functionality
    facilityTypeLoadModeRef.current = apiType === 'all' ? 'all' : null;
    let cachedAllFacilities: any[] | null = null;
    let cachedAllCounts: Record<string, number> | null = null;
    let cachedAllTime = 0;
    
    try {
      // Fast path: if we already cached all base types locally, serve immediately
      if (apiType === 'all') {
        const cacheStart = Date.now();
        const cached = await cacheService.getCachedAllFacilities(selectedCounty || undefined, regionParam);
        cachedAllFacilities = cached.data;
        cachedAllCounts = cached.counts;
        cachedAllTime = Date.now() - cacheStart;

        if (cachedAllFacilities.length > 0) {
          const cachedResults = filterByFacilityRegion(cachedAllFacilities.map(mapFacilityToWell));
          if (cachedResults.length > 0) {
            console.log('💾 [CHIP CLICK] CACHE HIT (ALL):', {
              count: cachedResults.length,
              counts: cachedAllCounts,
              county: selectedCounty || 'all',
              time: `${cachedAllTime}ms`,
              region: regionParam
            });
            setSearchResults(cachedResults);
            setSearchCacheInfo({ count: cachedResults.length, time: cachedAllTime, cached: true });
            setSearchOffset(cachedResults.length);
            setSearchTotalCount(cachedResults.length);
            setSearchHasMore(false);
            setNoResultsQuery(null);
            setIsFacilityTypeLoading(false);
            setSearchLoading(false);
            setShowSearchResults(true);
            return;
          }
          console.log('ℹ️ [CHIP CLICK] Cached ALL facilities found but none matched region:', regionParam);
        }
      }

      // Build URL directly to avoid React state timing issues
      // IMPORTANT: For "all" type, use pagination to avoid freezing with 149k+ records
      const ALL_FACILITIES_PAGE_SIZE = 5000;
      let url = apiType === 'all' 
        ? `/api/facilities/search?type=${apiType}&limit=${ALL_FACILITIES_PAGE_SIZE}&offset=0`
        : `/api/facilities/search?type=${apiType}`;
      if (apiType === 'all') {
        url += '&fetchAll=true'; // get totals so pagination state stays accurate
      }
      
      // Add facility subtype if applicable (e.g., Well Head, Tank Battery)
      const facilitySubtype = FACILITY_SUBTYPE_MAP[label];
      if (apiType === 'facilities' && facilitySubtype) {
        url += `&facilityTypes=${facilitySubtype}`;
      }
      
      // Add county filter if selected
      if (selectedCounty) {
        url += `&county=${encodeURIComponent(selectedCounty)}`;
      }

      if (regionParam) {
        url += `&state=${regionParam}`;
      }
      
      console.log('🔍 [CHIP CLICK] URL built:', url);
      
      // Try cache only for base types (wells, swds, rigs), not facility subtypes
      let wells: Well[];
      const canUseCache = !facilitySubtype && (apiType === 'wells' || apiType === 'swds' || apiType === 'rigs');
      
      console.log('💾 [CHIP CLICK] Cache check:', { canUseCache, facilitySubtype, apiType, selectedCounty });
      
      const cacheStartTime = Date.now();
      if (canUseCache) {
        const cached = await cacheService.getCachedData(
          selectedCounty || undefined, 
          apiType as 'wells' | 'swds' | 'rigs',
          regionParam
        );
        if (cached && cached.length > 0) {
          const cacheTime = Date.now() - cacheStartTime;
          const wellsFromCache = filterByFacilityRegion(cached.map(mapFacilityToWell));
          if (wellsFromCache.length > 0) {
            console.log('✅ [CHIP CLICK] CACHE HIT:', {
              label,
              count: wellsFromCache.length,
              county: selectedCounty || 'all',
              time: `${cacheTime}ms`,
              path: 'cache',
              region: regionParam
            });
            wells = wellsFromCache;
            setSearchResults(wellsFromCache);
            setSearchCacheInfo({ count: wellsFromCache.length, time: cacheTime, cached: true });
            setSearchLoading(false);
            setIsFacilityTypeLoading(false); // Hide CyberSpinner
            setShowSearchResults(true); // NOW show results with data ready
            console.log('🎯 [CHIP CLICK] Results set from cache, loading cleared, SearchResults should display');
            // FIXED: Don't return early - let function complete naturally
            return;
          }
          console.log('ℹ️ [CHIP CLICK] Cache hit but region had no entries, falling back to API.', { label, region: regionParam });
        } else {
          console.log('❌ [CHIP CLICK] Cache miss:', { cached: !!cached, length: cached?.length || 0 });
        }
      }
      
      // Track fetch time for API calls
      const fetchStartTime = Date.now();
      
      // Special case: Wells type uses compact streaming for all 60k+ records
      if (apiType === 'wells' && !selectedCounty) {
        console.log('🌐 [CHIP CLICK] Using compact stream for all wells...');
        const compactUrl = '/api/facilities/search?type=wells&format=compact';
        const response = await fetch(compactUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        wells = filterByFacilityRegion(await parseCompactWellsStream(response));
        const fetchTime = Date.now() - fetchStartTime;
        
        console.log('✅ [CHIP CLICK] Compact stream results:', { label, count: wells.length, fetchTime: fetchTime + 'ms', region: regionParam });
        setSearchCacheInfo({ count: wells.length, time: fetchTime, cached: false });
        setSearchResults(wells);
        setIsFacilityTypeLoading(false); // Hide CyberSpinner
        setShowSearchResults(true); // NOW show results with data ready
        
        // Cache for future use
        if (wells.length > 0 && !isOffline) {
          cacheService.cacheFacilityType('wells', undefined, wells)
            .catch(err => console.error('❌ Failed to cache wells:', err));
        }
        return;
      }
      
      // Fetch from API (regular JSON for other types)
      console.log('🌐 [CHIP CLICK] Fetching from API...');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      const fetchTime = Date.now() - fetchStartTime;
      
      // Validate response structure
      console.log('📦 [CHIP CLICK] API response:', { 
        success: data.success, 
        facilitiesCount: data.facilities?.length || 0,
        total: data.total,
        hasMore: data.hasMore,
        fetchTime: fetchTime + 'ms',
        hasError: !!data.error,
        searchStats: data.searchStats,
        errors: data.searchStats?.errors,
        path: 'api'
      });
      
      // For "all" type, set pagination info so user can load more
      if (apiType === 'all') {
        const totalCount = data.total || data.count || 0;
        setSearchTotalCount(totalCount);
        setSearchOffset(data.facilities?.length || 0);
        setSearchHasMore(data.hasMore || (data.facilities?.length || 0) < totalCount);
        console.log('📊 [CHIP CLICK] Pagination set:', { 
          total: totalCount, 
          loaded: data.facilities?.length, 
          hasMore: data.hasMore 
        });
      }
      
      // Detect empty dataset issues
      if (data.success && (!data.facilities || data.facilities.length === 0)) {
        console.warn('⚠️ [CHIP CLICK] API returned success but no results for:', label);
        console.warn('   This may indicate:', {
          possibleCauses: [
            'Empty database table (SWD/Rig tables may not be populated)',
            'No matching facilities for this type',
            'County filter too restrictive',
            'Facility subtype code incorrect'
          ],
          searchStats: data.searchStats,
          apiType,
          facilitySubtype
        });
      }
      
      if (!data.success && data.error) {
        console.error('❌ [CHIP CLICK] API returned error:', data.error);
        throw new Error(data.error);
      }
      
      const facilities = (data.facilities || []) as Array<{ type?: string }>;
      
      // If API came back suspiciously small (e.g., only rigs), fall back to cached full dataset
      if (apiType === 'all' && cachedAllFacilities) {
        const typeCounts = facilities.reduce<Record<string, number>>((acc: Record<string, number>, f: { type?: string }) => {
          const type = (f?.type || 'unknown').toLowerCase();
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        const uniqueTypes = Object.keys(typeCounts);
        const rigOnly = uniqueTypes.length === 1 && uniqueTypes[0] === 'rig';
        const cacheHasMore = cachedAllFacilities.length > facilities.length;

        if (cacheHasMore && rigOnly) {
          console.warn('⚠️ [CHIP CLICK] ALL facilities API returned only rigs; using cached dataset instead', {
            apiCount: facilities.length,
            cachedCount: cachedAllFacilities.length,
            cachedAllCounts
          });
          const cachedResults = filterByFacilityRegion(cachedAllFacilities.map(mapFacilityToWell));
          setSearchResults(cachedResults);
          setSearchCacheInfo({ count: cachedResults.length, time: cachedAllTime, cached: true });
          setSearchOffset(cachedResults.length);
          setSearchTotalCount(cachedResults.length);
          setSearchHasMore(false);
          setNoResultsQuery(null);
          setIsFacilityTypeLoading(false);
          setShowSearchResults(true);
          return;
        }
      }

      // Special handling for known empty tables
      if (facilities.length === 0) {
        const emptyTableMessages: Record<string, string> = {
          'swds': 'SWD table may be empty. Run import-swds.sh to populate data.',
          'rigs': 'Rig table may be empty. Run import-rigs.sh to populate data.',
        };
        
        if (emptyTableMessages[apiType]) {
          console.warn('⚠️ [CHIP CLICK]', emptyTableMessages[apiType]);
        }
        
        if (apiType === 'facilities' && facilitySubtype === 'WH') {
          console.warn('⚠️ [CHIP CLICK] No Well Head facilities found. Check if Facility table has "- (WH)" type records.');
        }
      }
      
      if (!Array.isArray(facilities)) {
        console.error('❌ [CHIP CLICK] Invalid response format:', data);
        throw new Error('Invalid response format from API');
      }
      
      wells = filterByFacilityRegion(facilities.map(mapFacilityToWell));
      
      // Set fetch info for inline display (all facility types show timing)
      setSearchCacheInfo({ count: wells.length, time: fetchTime, cached: false });

      if (apiType === 'all') {
        setSearchOffset(wells.length);
        setSearchTotalCount(wells.length);
        setSearchHasMore(false);
      }
      
      // Cache for future use (only base types)
      if (wells.length > 0 && !isOffline && canUseCache) {
        console.log('💾 [CHIP CLICK] Caching results:', { count: wells.length, type: apiType });
        await cacheService.cacheFacilityType(
          apiType as 'wells' | 'swds' | 'rigs',
          selectedCounty || undefined,
          wells
        );
      }
      
      console.log('✅ [CHIP CLICK] API results:', { label, count: wells.length, path: 'api' });
      setSearchResults(wells);
      setIsFacilityTypeLoading(false); // Hide CyberSpinner
      setShowSearchResults(true); // NOW show results with data ready
      
    } catch (error) {
      console.error('❌ [CHIP CLICK] ERROR:', {
        label,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      telegram.showAlert(`Could not search for ${label}. Please try again.`);
      setSearchResults([]);
      setShowSearchResults(false);
      setIsFacilityTypeLoading(false); // Hide CyberSpinner on error too
    } finally {
      setSearchLoading(false);
      setIsFacilityTypeLoading(false); // Safety: ensure spinner is hidden
      console.log('🏁 [CHIP CLICK] Complete:', label, '| Status after:', status, '| Voice still NOT activated');
    }
  };

  // Handle "Wells" click - opens WellBrowser with fast letter-based browsing
  // Also updates selected state so the Wells chip stays highlighted
  // COMMENTED: No longer used - replaced with individual Well/Rig/SWD buttons
  // const handleShowAllClick = () => {
  //   telegram.hapticFeedback('light');
  //   setSelectedFacilityType('wells');
  //   setSelectedFacilityLabel('Wells');
  //   setShowWellBrowser(true);
  // };

  // Toast handler
  const handleToastClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast({ open: false, message: '' });
  };

  // NOTE: These cache management functions were moved to OfflineCacheManager component
  // Keeping references for basinDownloadLoading state management
  void basinDownloadLoading; // State used by OfflineCacheManager via props

  const containerPadding = openDrawer
    ? { xs: 2, sm: 3, md: 4 }
    : { xs: 11, sm: 12, md: 14 };

  return (
    <Box
      key={`main-container-${darkMode ? 'dark' : 'light'}`}
      sx={{ 
        minHeight: '100vh',
        background: `${cyber.gradientGlow}, ${cyber.gradientBg}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'background 0.3s ease-in-out',
      }}
    >
      {/* Animated background orbs */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', opacity: 0.6 }}>
        <Box sx={{ 
          position: 'absolute', 
          top: '10%', 
          right: '15%', 
          width: '400px', 
          height: '400px', 
          background: 'radial-gradient(circle, rgba(96, 165, 250, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
            '33%': { transform: 'translate(30px, -30px) scale(1.1)' },
            '66%': { transform: 'translate(-20px, 20px) scale(0.9)' }
          },
          animation: 'float 20s ease-in-out infinite'
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: '20%', 
          left: '10%', 
          width: '350px', 
          height: '350px', 
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
          animation: 'float 25s ease-in-out infinite reverse'
        }} />
      </Box>

      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: 4,
          position: 'relative',
          zIndex: 1,
          pl: containerPadding,
          pr: containerPadding,
          transition: 'padding 0.3s ease'
        }}
      >
        {/* Expired Trial / Upgrade Banner */}
        <ExpiredTrialBanner />
        
        {/* Status Indicators */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3, 
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Offline Indicator */}
          {isOffline && (
            <Chip
              icon={<WifiOffIcon />}
              label="Offline Mode"
              color="warning"
              size="small"
              sx={{
                backgroundColor: 'rgba(251, 191, 36, 0.2)',
                color: '#fbbf24',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                fontWeight: 600,
              }}
            />
          )}
          

        </Box>

        {/* ==================== FULL-SCREEN CYBERSPINNER FOR FACILITY TYPE LOADING ==================== */}
        {/* Shows ONLY a spinner on blank themed background - nothing else visible */}
        {isFacilityTypeLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: darkMode ? '#0a0a0f' : '#f8fafc',
              zIndex: 99999,
            }}
          >
            <CyberSpinner size={120} speed="rapid" />
          </Box>
        )}

        {/* ==================== CANVAS - Clean Main Page ==================== */}
        {/* FABs control this canvas. CyberSpinner + Recent Search Gradient Chips */}
        {/* Hide canvas when full-screen facility type loading is active to prevent spinner overlap */}
        {!isFacilityTypeLoading && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center', maxWidth: '600px', px: 2, width: '100%' }}>
            
            {/* CyberSpinner - Slow when idle, fast when active, celebrates on success */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={handleVoiceButtonPress}
                onKeyDown={handleSpinnerKeyDown}
                sx={{
                  borderRadius: '50%',
                  display: 'inline-flex',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  transform: status !== 'idle' ? 'scale(1.25)' : 'scale(1)',
                  '&:active': { transform: 'scale(0.95)' },
                  '&:focus-visible': {
                    boxShadow: `0 0 0 4px ${cyber.neonCyan}60`,
                  },
                }}
              >
                <CyberSpinner 
                  size={status !== 'idle' ? 150 : 120} 
                  speed={status === 'idle' ? 'slow' : status === 'processing' ? 'rapid' : 'fast'} 
                  celebrateSuccess={status === 'success'} 
                />
                {/* Fingerprint icon centered - indicates tap to activate voice */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <FingerprintIcon 
                    sx={{ 
                      fontSize: status !== 'idle' ? 60 : 48,
                      color: status === 'success' ? cyber.neonGreen :
                             status !== 'idle' ? activeFingerprintColor :
                             fingerprintColor,
                      filter: status === 'success' 
                        ? `drop-shadow(0 0 12px ${cyber.neonGreen})` 
                        : status !== 'idle' 
                          ? `drop-shadow(0 0 15px ${activeFingerprintColor})` 
                          : `drop-shadow(0 0 6px ${fingerprintColor}60)`,
                      transition: 'all 0.3s ease',
                    }} 
                  />
                </Box>
              </Box>
            </Box>

            {/* Error Message */}
            {errorMessage && (
              <Fade in>
                <Paper 
                  elevation={4}
                  sx={{ 
                    p: 2, 
                    mb: 3,
                    backgroundColor: `${cyber.neonPink}20`,
                    border: `1px solid ${cyber.neonPink}40`
                  }}
                >
                  <Typography variant="body1" color={cyber.neonPink}>
                    {errorMessage}
                  </Typography>
                </Paper>
              </Fade>
            )}

            {/* Clear All Button - Only show if there's history */}
            {searchHistory.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label="Clear All"
                  size="small"
                  onClick={() => {
                    feedbackService.feedback('delete', 'medium');
                    offlineService.clearRichSearchHistory();
                    setSearchHistory([]);
                  }}
                  sx={{
                    backgroundColor: `${cyber.neonPink}15`,
                    color: cyber.textMuted,
                    border: `1px solid ${cyber.neonPink}30`,
                    fontSize: '0.75rem',
                    height: '28px',
                    '&:hover': {
                      backgroundColor: `${cyber.neonPink}30`,
                      color: cyber.textPrimary,
                    }
                  }}
                />
              </Box>
            )}

            {/* Gradient Search Chips - Recent searches with facility type proportions */}
            {searchHistory.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1.5, 
                justifyContent: 'center',
                maxWidth: '500px',
                mx: 'auto'
              }}>
                {searchHistory.slice(0, 25).map((item, idx) => (
                  <Fade in key={`${item.query}-${item.timestamp}`} timeout={300 + idx * 50}>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{ 
                            maxWidth: '120px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.query}
                          </span>
                          <span style={{ 
                            fontSize: '0.7rem', 
                            opacity: 0.8,
                            fontWeight: 400
                          }}>
                            ({item.totalCount})
                          </span>
                        </Box>
                      }
                      onClick={() => {
                        feedbackService.feedback('search', 'light');
                        handleSearchCommand(item.query);
                      }}
                      onDelete={() => {
                        feedbackService.feedback('delete', 'light');
                        offlineService.removeRichSearchHistory(item.query);
                        setSearchHistory(offlineService.getRichSearchHistory());
                      }}
                      deleteIcon={
                        <CloseIcon sx={{ 
                          fontSize: 24,  // Large 52px touch target via padding
                          p: '14px',
                          m: '-14px',
                          mr: '-10px'
                        }} />
                      }
                      sx={{
                        background: calculateGradient(item.facilityBreakdown, cyber.neonBlue),
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        height: '44px',
                        minWidth: '100px',
                        px: 1.5,
                        borderRadius: '22px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                          cursor: 'pointer'
                        },
                        '&:active': {
                          transform: 'scale(0.98)'
                        },
                        '& .MuiChip-label': {
                          px: 1
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { 
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            borderRadius: '50%'
                          }
                        }
                      }}
                    />
                  </Fade>
                ))}
              </Box>
            )}

            {/* Empty state hint - encourage voice search - Spraypaint style */}
            {searchHistory.length === 0 && status === 'idle' && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mt: 3,
                    fontFamily: '"Permanent Marker", "Brush Script MT", cursive',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonPurple} 50%, ${cyber.neonPink} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textShadow: `0 0 20px ${cyber.neonCyan}40, 0 0 40px ${cyber.neonPurple}30`,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    transform: 'rotate(-2deg)',
                    animation: 'spraypaint 0.5s ease-out forwards',
                    '@keyframes spraypaint': {
                      '0%': { 
                        opacity: 0, 
                        transform: 'rotate(-2deg) scale(0.8)',
                        filter: 'blur(4px) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      },
                      '100%': { 
                        opacity: 1, 
                        transform: 'rotate(-2deg) scale(1)',
                        filter: 'blur(0px) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }
                    }
                  }}
                >
                  {language === 'es' ? 'Búsqueda por Voz' : 'Voice Search'}
                </Typography>
                <Typography 
                  variant="body2"
                  sx={{ 
                    mt: 0.5,
                    fontFamily: '"Permanent Marker", "Brush Script MT", cursive',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonPurple} 50%, ${cyber.neonPink} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textShadow: `0 0 20px ${cyber.neonCyan}40, 0 0 40px ${cyber.neonPurple}30`,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    transform: 'rotate(-2deg)',
                    animation: 'spraypaint 0.5s ease-out forwards 0.1s',
                    opacity: 0,
                    '@keyframes spraypaint': {
                      '0%': { 
                        opacity: 0, 
                        transform: 'rotate(-2deg) scale(0.8)',
                        filter: 'blur(4px) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      },
                      '100%': { 
                        opacity: 1, 
                        transform: 'rotate(-2deg) scale(1)',
                        filter: 'blur(0px) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                      }
                    }
                  }}
                >
                  (iOS)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        )}
        {/* ==================== END CANVAS ==================== */}
        
        {/* Basin Selection Dialog */}
        <Dialog 
          open={showBasinDialog} 
          onClose={() => { telegram.hapticFeedback('light'); setShowBasinDialog(false); }}
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgCard,
              backgroundImage: 'none',
              maxWidth: '400px',
              width: '90%'
            }
          }}
        >
          <DialogTitle sx={{ color: cyber.textPrimary, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DownloadIcon color="primary" />
              <Typography variant="h6">{t('downloadOfflineMap')}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Typography variant="body2" sx={{ px: 3, pt: 2, pb: 2, color: cyber.textSecondary }}>
              {t('selectBasin')}
            </Typography>
            <List sx={{ pt: 0 }}>
              {Object.entries(BASINS).map(([key, basin]) => (
                <ListItem key={key} disablePadding>
                  <ListItemButton 
                    onClick={() => handleBasinSelect(key as Basin)}
                    sx={{
                      py: 2,
                      '&:hover': {
                        backgroundColor: `${cyber.neonBlue}10`
                      }
                    }}
                  >
                    <ListItemText
                      primary={basin.name}
                      secondary={basin.description}
                      primaryTypographyProps={{
                        color: cyber.textPrimary,
                        fontWeight: 600
                      }}
                      secondaryTypographyProps={{
                        color: cyber.textSecondary
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => { telegram.hapticFeedback('light'); setShowBasinDialog(false); }}
              sx={{ color: cyber.textSecondary }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* County Filter Dialog */}
        <Dialog 
          open={showCountyDialog} 
          onClose={() => { telegram.hapticFeedback('light'); setShowCountyDialog(false); }}
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgCard,
              backgroundImage: 'none',
              maxWidth: '400px',
              width: '90%'
            }
          }}
        >
          <DialogTitle sx={{ color: cyber.textPrimary, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon color="primary" />
              <Typography variant="h6">{t('filterByCounty')}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Typography variant="body2" sx={{ px: 3, pt: 2, pb: 2, color: cyber.textSecondary }}>
              {t('selectCounty')}
            </Typography>
            <List sx={{ pt: 0 }}>
              {/* All Counties Option */}
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => {
                    telegram.hapticFeedback('light');
                    setSelectedCounty(null);
                    setShowCountyDialog(false);
                  }}
                  selected={selectedCounty === null}
                  sx={{
                    py: 2,
                    '&:hover': {
                      backgroundColor: `${cyber.neonBlue}10`
                    },
                    '&.Mui-selected': {
                      backgroundColor: `${cyber.neonBlue}20`,
                      '&:hover': {
                        backgroundColor: `${cyber.neonBlue}30`
                      }
                    }
                  }}
                >
                  <ListItemText
                    primary="All Counties"
                    secondary="Search entire New Mexico (139,327 wells)"
                    primaryTypographyProps={{
                      color: cyber.textPrimary,
                      fontWeight: 600
                    }}
                    secondaryTypographyProps={{
                      color: cyber.textSecondary
                    }}
                  />
                </ListItemButton>
              </ListItem>
              
              {/* Individual Counties */}
              {NEW_MEXICO_COUNTIES.map((county) => (
                <ListItem key={county.name} disablePadding>
                  <ListItemButton 
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSelectedCounty(county.name);
                      setShowCountyDialog(false);
                    }}
                    selected={selectedCounty === county.name}
                    sx={{
                      py: 2,
                      '&:hover': {
                        backgroundColor: `${cyber.neonBlue}10`
                      },
                      '&.Mui-selected': {
                        backgroundColor: `${cyber.neonBlue}20`,
                        '&:hover': {
                          backgroundColor: `${cyber.neonBlue}30`
                        }
                      }
                    }}
                  >
                    <ListItemText
                      primary={`${county.name} County`}
                      secondary={`${county.basin} Basin • ${county.wells.toLocaleString()} wells`}
                      primaryTypographyProps={{
                        color: cyber.textPrimary,
                        fontWeight: 600
                      }}
                      secondaryTypographyProps={{
                        color: cyber.textSecondary
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => { telegram.hapticFeedback('light'); setShowCountyDialog(false); }}
              sx={{ color: cyber.textSecondary }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Search Results Dialog */}
        <SearchResults
          open={showSearchResults}
          wells={sortedSearchResults}
          query={searchQuery}
          sortOrder={sortOrder}
          facilityTypeLabel={selectedFacilityLabel}
          cacheInfo={searchCacheInfo}
          loading={searchLoading}
          totalCount={searchTotalCount}
          hasMore={searchHasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          onClose={handleCloseSearchResults}
          onSelectWell={handleWellSelect}
          onViewOnMap={handleViewOnMap}
          onToggleSort={handleToggleSort}
          onToggleFavorite={toggleFavorite}
        />

        {/* Fast Well Browser with A-Z Scrubber */}
        <WellBrowser
          open={showWellBrowser}
          onClose={() => setShowWellBrowser(false)}
          onSelectWell={(well) => {
            setShowWellBrowser(false);
            handleWellSelect(well);
          }}
          onViewOnMap={(well) => {
            setShowWellBrowser(false);
            handleViewOnMap(well);
          }}
          onToggleFavorite={toggleFavorite}
        />

        {/* Active Operations - Real Data Dashboard */}
        <ActiveOperations
          open={showActiveOperations}
          onClose={() => setShowActiveOperations(false)}
          onNavigateToZone={(lat, lon) => {
            setShowActiveOperations(false);
            // Navigate to the zone location
            const zoneWell: Well = {
              id: `zone-${lat}-${lon}`,
              api: '',
              wellName: 'Activity Zone',
              latitude: lat,
              longitude: lon,
              county: '',
              operator: '',
              status: '',
              basin: '',
              type: 'well'
            };
            handleWellSelect(zoneWell);
          }}
        />

        {/* Favorites Panel */}
        <FavoritesPanel
          open={showFavorites}
          favorites={favorites}
          loading={favoritesLoading}
          onClose={() => setShowFavorites(false)}
          onSelectWell={handleWellSelect}
          onRemoveFavorite={removeFromFavorites}
          onViewOnMap={(well) => {
            setShowFavorites(false);
            handleViewOnMap(well);
          }}
          onAddNew={() => {
            setShowFavorites(false);
            setShowSearchMode(true);
          }}
        />

        {/* Favorites Map View - with road hazards */}
        <FavoritesMapView
          open={showFavoritesMap}
          favorites={favorites}
          onClose={() => setShowFavoritesMap(false)}
          onSelectWell={handleWellSelect}
          roadHazards={roadHazards}
          onRouteCreated={() => {
            // Increment refresh key to trigger CommunityRoutesPanel re-fetch
            setCalicheRoutesRefreshKey(prev => prev + 1);
          }}
        />

        {/* Crews Panel */}
        <Dialog
          open={showCrews}
          onClose={() => setShowCrews(false)}
          fullScreen
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgPrimary,
              backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: cyber.textPrimary, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${cyber.borderDefault}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ color: cyber.neonBlue }} />
              My Crews
            </Box>
            <IconButton onClick={() => setShowCrews(false)} sx={{ color: cyber.neonBlue }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <CrewPanel 
              currentLocation={currentLocation}
              onNavigateToLocation={(lat, lng, name) => {
                setShowCrews(false);
                // Navigate to the meeting location
                if (lat && lng) {
                  const meetingWell: Well = {
                    id: `meeting-${Date.now()}`,
                    api: 'MEETING',
                    wellName: name || 'Meeting Point',
                    latitude: lat,
                    longitude: lng,
                    county: 'Unknown',
                    operator: 'Crew Meeting',
                    status: 'active',
                    basin: 'Unknown'
                  };
                  handleWellSelect(meetingWell);
                }
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Services Marketplace */}
        <ServicesMarketplace
          open={showServices}
          onClose={() => setShowServices(false)}
          userLocation={currentLocation || undefined}
          darkMode={darkMode}
          language={language}
        />

        {/* Jobs Board */}
        <JobsBoard
          open={showJobsBoard}
          onClose={() => setShowJobsBoard(false)}
        />

        {/* Friends Panel */}
        <FriendsPanel
          open={showFriends}
          onClose={() => setShowFriends(false)}
          currentLocation={currentLocation}
        />

        {/* Basin Community Panel - Morning Pulse + Basin Feed */}
        <Dialog
          open={showBasinCommunity}
          onClose={() => setShowBasinCommunity(false)}
          fullScreen
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgPrimary,
              backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: cyber.textPrimary, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${cyber.borderDefault}`,
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ color: '#60a5fa' }} />
              Basin Community
            </Box>
            <IconButton onClick={() => setShowBasinCommunity(false)} sx={{ color: '#60a5fa' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            {/* Morning Pulse - greeting and awareness */}
            <MorningPulse 
              basin="permian"
              onNavigateToHazard={(lat, lng) => {
                setShowBasinCommunity(false);
                // Navigate to hazard location using same pattern as crew meetings
                const hazardLocation: Well = {
                  id: `hazard-${Date.now()}`,
                  api: 'HAZARD',
                  wellName: 'Road Hazard',
                  latitude: lat,
                  longitude: lng,
                  county: 'Unknown',
                  operator: 'Hazard Alert',
                  status: 'active',
                  basin: 'Unknown'
                };
                handleWellSelect(hazardLocation);
              }}
            />
            
            {/* Basin Feed - detailed hazard/activity feed */}
            <Box sx={{ mt: 2 }}>
              <BasinFeed 
                basin="permian"
                maxHeight={400}
                onNavigateToHazard={(lat, lng) => {
                  setShowBasinCommunity(false);
                  // Navigate to hazard location using same pattern as crew meetings
                  const hazardLocation: Well = {
                    id: `hazard-${Date.now()}`,
                    api: 'HAZARD',
                    wellName: 'Road Hazard',
                    latitude: lat,
                    longitude: lng,
                    county: 'Unknown',
                    operator: 'Hazard Alert',
                    status: 'active',
                    basin: 'Unknown'
                  };
                  handleWellSelect(hazardLocation);
                }}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Facilities Full-Screen Dialog - Facility Types Only */}
        <Dialog
          open={showSearchMode}
          onClose={() => setShowSearchMode(false)}
          fullScreen
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgPrimary,
              backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: cyber.textPrimary, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${cyber.borderDefault}`,
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalGasStationIcon sx={{ color: cyber.neonCyan }} />
              {facilityRegion ? `Facilities — ${facilityRegionLabel}` : 'Choose Facility Region'}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {facilityRegion && (
                <Button
                  onClick={() => setFacilityRegion(null)}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    color: cyber.neonCyan,
                    borderColor: `${cyber.neonCyan}80`,
                    '&:hover': {
                      borderColor: cyber.neonCyan
                    }
                  }}
                >
                  Change Region
                </Button>
              )}
              <IconButton onClick={() => setShowSearchMode(false)} sx={{ color: cyber.neonCyan }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            {!facilityRegion ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ color: cyber.textSecondary, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Pick where you are working so we can load the right facilities.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                  {facilityRegionOptions.map((region) => (
                    <Card
                      key={region.code}
                      onClick={() => handleFacilityRegionSelect(region.code)}
                      sx={{
                        p: { xs: 2.2, sm: 3 },
                        backgroundColor: cyber.bgCard,
                        borderRadius: 3,
                        cursor: 'pointer',
                        border: `1px solid ${cyber.borderDefault}`,
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border 0.25s ease',
                        boxShadow: `0 16px 36px ${withAlpha(region.accent, 0.35)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 1.8, sm: 2.2 },
                        minHeight: { xs: 220, sm: 240 },
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 24px 48px ${withAlpha(region.accent, 0.6)}`,
                          border: `1px solid ${withAlpha(region.accent, 0.85)}`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ color: region.accent, fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.45rem' }, letterSpacing: '0.02em' }}>
                          {region.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.4,
                              borderRadius: 999,
                              fontSize: '0.75rem',
                              letterSpacing: '0.16em',
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              color: withAlpha(region.accent, 0.95),
                              backgroundColor: withAlpha(region.accent, 0.15),
                              border: `1px solid ${withAlpha(region.accent, 0.35)}`,
                            }}
                          >
                            {region.code}
                          </Box>
                        </Box>
                      </Box>
                      <Typography sx={{ color: cyber.textSecondary, fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.5 }}>
                        {region.blurb}
                      </Typography>
                      {renderRegionBadge(region)}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 1 }}>
                        <Typography sx={{ color: withAlpha(region.accent, 0.9), fontSize: { xs: '0.8rem', sm: '0.85rem' }, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>
                          Tap to activate region grid
                        </Typography>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${withAlpha(region.accent, 0.5)}`,
                            backgroundColor: withAlpha(region.accent, 0.18),
                          }}
                        >
                          <ArrowForwardIcon sx={{ fontSize: 20, color: withAlpha(region.accent, 0.95) }} />
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)', md: 'repeat(8, 1fr)' },
              gap: { xs: 0.75, sm: 1, md: 1.5 },
              px: { xs: 0.5, sm: 1 }
            }}>
              {[
                { icon: LocalGasStationIcon, label: 'Well', translationKey: 'well', color: '#f59e0b', onClick: () => { handleFacilityTypeClick('Wells'); setShowSearchMode(false); } },
                { icon: ConstructionIcon, label: 'Rig', translationKey: 'rig', color: '#fbbf24', onClick: () => { handleFacilityTypeClick('Rigs'); setShowSearchMode(false); } },
                { icon: PrecisionManufacturingIcon, label: 'Fracking Site', translationKey: 'frackingSite', color: '#ec4899', onClick: () => { handleFacilityTypeClick('Fracking Site'); setShowSearchMode(false); } },
                { icon: BuildIcon, label: 'Drilling Site', translationKey: 'drillingSite', color: '#f97316', onClick: () => { handleFacilityTypeClick('Drilling Site'); setShowSearchMode(false); } },
                { icon: WaterIcon, label: 'SWD', translationKey: 'swd', color: '#10b981', onClick: () => { handleFacilityTypeClick('SWDs'); setShowSearchMode(false); } },
                { icon: OpacityIcon, label: 'Brine Well', translationKey: 'brineWell', color: '#0ea5e9', onClick: () => { handleFacilityTypeClick('Brine Well'); setShowSearchMode(false); } },
                { icon: AdjustIcon, label: 'Well Head', translationKey: 'wellHead', color: '#f59e0b', onClick: () => { handleFacilityTypeClick('Well Head'); setShowSearchMode(false); } },
                { icon: LocationOnIcon, label: 'Well Pad', translationKey: 'wellPad', color: '#8b5cf6', onClick: () => { handleFacilityTypeClick('Well Pad'); setShowSearchMode(false); } },
                { icon: BoltIcon, label: 'Tank Battery', translationKey: 'tankBattery', color: '#06b6d4', onClick: () => { handleFacilityTypeClick('Tank Battery'); setShowSearchMode(false); } },
                { icon: PropaneTankIcon, label: 'Gas Storage', translationKey: 'gasStorage', color: '#fbbf24', onClick: () => { handleFacilityTypeClick('Gas Storage'); setShowSearchMode(false); } },
                { icon: LandscapeIcon, label: 'Below Grade', translationKey: 'belowGradeTank', color: '#78716c', onClick: () => { handleFacilityTypeClick('Below Grade Tank'); setShowSearchMode(false); } },
                { icon: SettingsIcon, label: 'Compressor', translationKey: 'compressor', color: '#60a5fa', onClick: () => { handleFacilityTypeClick('Compressor'); setShowSearchMode(false); } },
                { icon: AirIcon, label: 'Crude Pump', translationKey: 'crudePump', color: '#64748b', onClick: () => { handleFacilityTypeClick('Crude Pump'); setShowSearchMode(false); } },
                { icon: FactoryIcon, label: 'Gas Plant', translationKey: 'gasPlant', color: '#a855f7', onClick: () => { handleFacilityTypeClick('Gas Plant'); setShowSearchMode(false); } },
                { icon: OilBarrelIcon, label: 'Refinery', translationKey: 'refinery', color: '#a855f7', onClick: () => { handleFacilityTypeClick('Refinery'); setShowSearchMode(false); } },
                { icon: ScienceIcon, label: 'Treating', translationKey: 'treatingPlant', color: '#f472b6', onClick: () => { handleFacilityTypeClick('Treating Plant'); setShowSearchMode(false); } },
                { icon: MedicationLiquidIcon, label: 'Injection', translationKey: 'injectionPlant', color: '#34d399', onClick: () => { handleFacilityTypeClick('Injection Plant'); setShowSearchMode(false); } },
                { icon: WhatshotIcon, label: 'Flare Stack', translationKey: 'flareStack', color: '#ef4444', onClick: () => { handleFacilityTypeClick('Flare Stack'); setShowSearchMode(false); } },
                { icon: LinearScaleIcon, label: 'Gas Line', translationKey: 'gasPipeline', color: '#3b82f6', onClick: () => { handleFacilityTypeClick('Gas Pipeline'); setShowSearchMode(false); } },
                { icon: LinearScaleIcon, label: 'Crude Line', translationKey: 'crudePipeline', color: '#64748b', onClick: () => { handleFacilityTypeClick('Crude Pipeline'); setShowSearchMode(false); } },
                { icon: LinearScaleIcon, label: 'Water Line', translationKey: 'waterPipeline', color: '#0ea5e9', onClick: () => { handleFacilityTypeClick('Water Pipeline'); setShowSearchMode(false); } },
                { icon: HubIcon, label: 'Gas Gather', translationKey: 'gasGathering', color: '#3b82f6', onClick: () => { handleFacilityTypeClick('Gas Gathering'); setShowSearchMode(false); } },
                { icon: SpeedIcon, label: 'Metering', translationKey: 'meteringStation', color: '#8b5cf6', onClick: () => { handleFacilityTypeClick('Metering Station'); setShowSearchMode(false); } },
                { icon: LandscapeIcon, label: 'Pits', translationKey: 'pits', color: '#78716c', onClick: () => { handleFacilityTypeClick('Pits'); setShowSearchMode(false); } },
                { icon: DeleteIcon, label: 'Waste Mgmt', translationKey: 'wasteMgmt', color: '#ef4444', onClick: () => { handleFacilityTypeClick('Waste Mgmt'); setShowSearchMode(false); } },
                { icon: RecyclingIcon, label: 'Recycling', translationKey: 'recycling', color: '#22c55e', onClick: () => { handleFacilityTypeClick('Recycling'); setShowSearchMode(false); } },
                { icon: HealingIcon, label: 'Remediate', translationKey: 'remediate', color: '#eab308', onClick: () => { handleFacilityTypeClick('Remediate'); setShowSearchMode(false); } },
                { icon: CleaningServicesIcon, label: 'Abatement', translationKey: 'abatement', color: '#f97316', onClick: () => { handleFacilityTypeClick('Abatement'); setShowSearchMode(false); } },
                { icon: EngineeringIcon, label: 'Service Co.', translationKey: 'serviceCo', color: '#64748b', onClick: () => { handleFacilityTypeClick('Service Co.'); setShowSearchMode(false); } },
                { icon: VerifiedIcon, label: 'Class I/II', translationKey: 'classIII', color: '#34d399', onClick: () => { handleFacilityTypeClick('Class I/II'); setShowSearchMode(false); } },
                { icon: LocalGasStationIcon, label: 'Wells', translationKey: 'facilities', color: '#ec4899', onClick: () => { handleFacilityTypeClick('facilities'); setShowSearchMode(false); }, renderEmoji: null },
                { icon: FastfoodIcon, label: 'Taco Truck', translationKey: 'tacoTruck', color: '#d946ef', onClick: () => { handleFacilityTypeClick('Taco Truck'); setShowSearchMode(false); } },
                { emoji: '💩', label: 'Porta Potty', translationKey: 'portaPotty', color: '#b91c1c', onClick: () => { handleFacilityTypeClick('Porta Potty'); setShowSearchMode(false); }, isEmoji: true }
              ].map((facility, idx) => {
                  const translation = t(facility.translationKey as any);
                  const displayLabel = translation && translation !== facility.translationKey ? translation : facility.label;
                return (
                  <Card
                    key={idx}
                    onClick={facility.onClick}
                    sx={{
                      p: { xs: 0.5, sm: 0.75, md: 1 },
                      textAlign: 'center',
                      backgroundColor: cyber.bgCard,
                      backdropFilter: 'blur(10px)',
                      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                      borderRadius: 2,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      boxShadow: `0 8px 20px ${facility.color}30`,
                      cursor: 'pointer',
                      aspectRatio: '1 / 1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: { xs: 'scale(1.02)', sm: 'scale(1.05)' },
                        boxShadow: `0 12px 28px ${facility.color}50`
                      }
                    }}
                  >
                    {(facility as any).isEmoji ? (
                      <Box sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, mb: 0.2, filter: `drop-shadow(0 0 12px ${facility.color}50)` }}>
                        {(facility as any).emoji}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.2, minHeight: { xs: 32, sm: 40, md: 48 } }}>
                        {React.createElement((facility as any).icon, {
                          sx: { fontSize: { xs: 28, sm: 32, md: 40 }, color: facility.color, filter: `drop-shadow(0 0 12px ${facility.color}50)` }
                        })}
                      </Box>
                    )}
                    <Typography sx={{ 
                      color: cyber.textSecondary, 
                      fontWeight: 600, 
                      fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                      lineHeight: 1.1,
                      fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                      px: 0.25
                    }}>
                      {displayLabel}
                    </Typography>
                  </Card>
                );
              })}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Manual Search Dialog - Text search with gradient chips */}
        <Dialog
          open={showManualSearch}
          onClose={() => setShowManualSearch(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: `1px solid ${darkMode ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
              maxHeight: '80vh',
              minHeight: '60vh',
              backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ 
            color: darkMode ? '#fff' : '#111', 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SearchIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
              <Typography variant="h5" sx={{ color: darkMode ? '#fff' : '#111', fontWeight: 600 }}>
                Search Facilities
              </Typography>
            </Box>
            <IconButton onClick={() => setShowManualSearch(false)} sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, overflowY: 'auto' }}>
            {/* Search Input */}
            <TextField
              fullWidth
              autoFocus
              placeholder={t('searchPlaceholderFull')}
              value={manualSearchQuery}
              onChange={(e) => setManualSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualSearchQuery.trim()) {
                  // Trigger search
                  handleSearchCommand(manualSearchQuery.trim());
                  setShowManualSearch(false);
                  feedbackService.playSound('success');
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: darkMode ? '#9ca3af' : '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: status === 'processing' ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                ) : null,
                sx: {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#06b6d4',
                  },
                  '& input': {
                    color: darkMode ? '#fff' : '#111',
                    fontSize: '1.1rem',
                  },
                  '& input::placeholder': {
                    color: darkMode ? '#9ca3af' : '#888',
                    opacity: 0.7,
                  }
                }
              }}
              sx={{ mb: 3 }}
            />

            {/* Quick Suggestions */}
            <Typography sx={{ color: darkMode ? '#9ca3af' : '#666', mb: 2, fontWeight: 600 }}>
              Quick Suggestions — Try saying:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {[
                // Location-based
                'Wells near Carlsbad',
                'SWDs in Lea County',
                'Rigs near Hobbs',
                // Operator searches
                'Show me EOG wells',
                'Chevron locations',
                'Oxy facilities nearby',
                // Status-based
                'Active drilling rigs',
                'Producing wells',
                'Permitted locations',
                // Type-specific
                'Saltwater disposal sites',
                'Injection wells',
                'Gas wells only',
                // Distance queries
                'Wells within 10 miles',
                'Closest SWD',
                'Nearest rig to me',
                // Basin/region
                'Delaware Basin wells',
                'Permian operators',
                // Combined queries
                'XTO wells in Eddy County',
                'Active wells near Jal',
                'Disposal wells accepting water',
              ].map((suggestion) => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  onClick={() => {
                    setManualSearchQuery(suggestion);
                    handleSearchCommand(suggestion);
                    setShowManualSearch(false);
                    feedbackService.playSound('success');
                  }}
                  sx={{
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                    color: darkMode ? '#fff' : '#333',
                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'}`,
                    fontSize: '0.85rem',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)',
                      borderColor: '#06b6d4',
                    }
                  }}
                />
              ))}
            </Box>

            {/* Recent Search History - Time Grouped with Clickable Facility Chips */}
            {searchHistory.length > 0 && (() => {
              // Time grouping logic
              const todayStart = new Date().setHours(0, 0, 0, 0);
              const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
              const threeDaysStart = todayStart - 3 * 24 * 60 * 60 * 1000;
              const weekStart = todayStart - 7 * 24 * 60 * 60 * 1000;

              const groupedHistory = {
                today: searchHistory.filter(h => h.timestamp >= todayStart),
                yesterday: searchHistory.filter(h => h.timestamp >= yesterdayStart && h.timestamp < todayStart),
                past3Days: searchHistory.filter(h => h.timestamp >= threeDaysStart && h.timestamp < yesterdayStart),
                thisWeek: searchHistory.filter(h => h.timestamp >= weekStart && h.timestamp < threeDaysStart),
                earlier: searchHistory.filter(h => h.timestamp < weekStart),
              };

              const openMapFiltered = (query: string, facilityType: string) => {
                // Execute search with type filter and open map
                handleSearchCommand(`${query} type:${facilityType}`);
                setShowManualSearch(false);
                setShowFavoritesMap(true);
                feedbackService.playSound('success');
              };

              const renderHistoryGroup = (title: string, items: typeof searchHistory) => {
                if (items.length === 0) return null;
                return (
                  <Box key={title} sx={{ mb: 3 }}>
                    <Typography sx={{ 
                      color: darkMode ? '#6b7280' : '#9ca3af', 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 1.5 
                    }}>
                      {title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {items.map((item, index) => (
                        <Box
                          key={`${title}-${index}`}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                          }}
                        >
                          {/* Query header - click to repeat search */}
                          <Box
                            onClick={() => {
                              setManualSearchQuery(item.query);
                              handleSearchCommand(item.query);
                              setShowManualSearch(false);
                              feedbackService.playSound('success');
                            }}
                            sx={{ 
                              cursor: 'pointer',
                              mb: 1.5,
                              '&:hover': { opacity: 0.8 }
                            }}
                          >
                            <Typography sx={{ 
                              color: darkMode ? '#fff' : '#111', 
                              fontWeight: 600, 
                              fontSize: '0.95rem' 
                            }}>
                              "{item.query}"
                            </Typography>
                            <Typography sx={{ 
                              color: darkMode ? '#6b7280' : '#9ca3af', 
                              fontSize: '0.7rem',
                              mt: 0.5
                            }}>
                              {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                          {/* Clickable facility type chips */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {Object.entries(item.facilityBreakdown).map(([type, count]) => {
                              const chipColor = FACILITY_COLORS[type.toLowerCase()] || '#64748b';
                              return (
                                <Chip
                                  key={type}
                                  label={`${count} ${type}`}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMapFiltered(item.query, type);
                                  }}
                                  sx={{
                                    backgroundColor: `${chipColor}${darkMode ? '25' : '18'}`,
                                    color: chipColor,
                                    border: `1.5px solid ${chipColor}60`,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: `${chipColor}${darkMode ? '40' : '30'}`,
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 0 12px ${chipColor}50`,
                                    },
                                    '&:active': {
                                      transform: 'scale(0.98)',
                                    }
                                  }}
                                />
                              );
                            })}
                            {/* Total chip - non-clickable */}
                            <Chip
                              label={`Total: ${item.totalCount}`}
                              size="small"
                              sx={{
                                backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                                color: darkMode ? '#9ca3af' : '#666',
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              };

              return (
                <>
                  <Typography sx={{ color: darkMode ? '#9ca3af' : '#666', mb: 2, fontWeight: 600 }}>
                    Recent Searches
                  </Typography>
                  {renderHistoryGroup('Today', groupedHistory.today)}
                  {renderHistoryGroup('Yesterday', groupedHistory.yesterday)}
                  {renderHistoryGroup('Past 3 Days', groupedHistory.past3Days)}
                  {renderHistoryGroup('This Week', groupedHistory.thisWeek)}
                  {renderHistoryGroup('Earlier', groupedHistory.earlier)}
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* ===== UNIFIED FAB MANAGER ===== */}
        <UnifiedFabManager
          visible={!openDrawer}
          onOpenDrawer={() => {
            telegram.hapticFeedback('light');
            setOpenDrawer(true);
          }}
          onShowSettings={() => {
            setShowSettingsPopout(!showSettingsPopout);
            feedbackService.tap();
          }}
          showSettingsPopout={showSettingsPopout}
          onFavoritesClick={() => setShowFavorites(true)}
          favoritesCount={favorites.length}
          voiceStatus={status}
          onVoiceClick={() => {
            handleVoiceButtonPress();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onSearchClick={() => setShowSearchMode(true)}
          onManualSearchClick={() => {
            setShowManualSearch(true);
            setManualSearchQuery('');
            feedbackService.tap();
          }}
          onMapClick={() => {
            if (favorites.length === 0) {
              telegram.showAlert(t('addFavoritesFirst'));
            } else {
              setShowFavoritesMap(true);
            }
          }}
          onCrewsClick={async () => {
            // Check premium feature access for Crew Management
            const hasAccess = await featureAccess.checkAccessWithPrompt('crewManagement');
            if (!hasAccess) {
              console.log('🔒 [CREWS] Crew Management locked - trial expired');
              return;
            }
            setShowCrews(true);
          }}
          onRoutesClick={() => setShowCaliches(true)}
          onCalicheRoutesClick={() => setShowCaliches(true)}
          onFacilitiesViewClick={() => setShowFacilitiesView(true)}
          onHazardsViewClick={() => setShowHazardsView(true)}
          onActivityClick={() => setShowActiveOperations(true)}
          onServicesClick={() => setShowServices(true)}
          onJobsClick={() => setShowJobsBoard(true)}
          onFriendsClick={() => setShowFriends(true)}
          onBasinCommunityClick={() => setShowBasinCommunity(true)}
          onCommunityFacilitiesClick={() => {
            telegram.hapticFeedback('light');
            setShowCommunityFacilities(true);
          }}
          communityFacilitiesCount={0}
          onHazardReported={(hazard) => {
            // Hazard was reported - show on map immediately
            console.log('[Hazard] Reported:', hazard.type, 'at', hazard.latitude, hazard.longitude);
            setRoadHazards(prev => [hazard, ...prev.filter(h => h.id !== hazard.id)]);
            // Open favorites map to show the hazard
            setShowFavoritesMap(true);
          }}
          onReportSafetyGuy={async () => {
            telegram.hapticFeedback('medium');
            let userLoc: { lat: number; lng: number } | null = currentLocation ?? null;

            try {
              const result = await requestPreciseLocation({
                desiredAccuracyMeters: 20,
                refinementTimeoutMs: 7000,
                allowLastKnownFallback: true,
                allowTelegramFallback: true,
              });

              if (result.fix) {
                userLoc = { lat: result.fix.lat, lng: result.fix.lng };
              } else if (!userLoc) {
                userLoc = { lat: 34.5199, lng: -105.8701 };
              }
            } catch (err) {
              console.error('GPS error:', err);
              if (!userLoc) {
                userLoc = { lat: 34.5199, lng: -105.8701 };
              }
            }

            // Immediately report safety inspector at current location
            try {
              const hazard = await roadHazardsService.reportHazard(
                'safety_inspector',
                userLoc.lat,
                userLoc.lng,
                'Safety inspector spotted',
                telegram.getUserId() || undefined,
                telegram.getUser()?.first_name || 'Anonymous'
              );
              
              if (hazard) {
                setRoadHazards(prev => [hazard, ...prev.filter(h => h.id !== hazard.id)]);
                setShowFavoritesMap(true);
                telegram.hapticFeedback('success');
              }
            } catch (error) {
              console.error('[VoiceNavigation] Error reporting safety inspector:', error);
              telegram.showAlert('Failed to report safety inspector');
            }
          }}
          onShowQrCode={() => {
            setShowQRShare(true);
            feedbackService.tap();
          }}
          onShowSuggestions={() => {
            setShowSuggestions(true);
            feedbackService.tap();
          }}
          // Premium/Subscription
          onPremiumClick={() => {
            setShowSubscription(true);
            feedbackService.tap();
          }}
          isSubscribed={subscriptionStatus?.isSubscribed || false}
          networkStatus={networkStatus}
          onHomeReset={() => {
            feedbackService.playSound('navigate');
            telegram.hapticFeedback('light');
            setShowSearchResults(false);
            setShowWellBrowser(false);
            setShowFavorites(false);
            setShowFavoritesMap(false);
            setShowBasinDialog(false);
            setShowCountyDialog(false);
            setShowCrews(false);
            setShowActiveOperations(false);
            setShowServices(false);
            setShowSearchMode(false);
            setShowCommunityRoutes(false);
            setShowCommunityFacilities(false);
            setShowAddFacility(false);
            setOpenDrawer(false);
            setSearchResults([]);
            setSearchQuery('');
            setEditableQuery('');
            setStatus('idle');
            setErrorMessage('');
          }}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
          highContrast={highContrast}
          onToggleHighContrast={toggleHighContrast}
          language={language}
          onToggleLanguage={toggleLanguage}
          onOpenZiaGivesBack={() => {
            telegram.hapticFeedback('light');
            setShowCharityDashboard(true);
          }}
          aiSpeedDial={<AISpeedDial />}
        />
        {/* End UNIFIED FAB MANAGER */}

        {/* Community Facilities Full-Screen Dialog */}
        <CommunityFacilitiesFab 
          externalOpen={showCommunityFacilities}
          onExternalOpenChange={setShowCommunityFacilities}
          onNavigate={(lat, lng, name) => {
            const favoriteWell: Well = {
              id: `community_${name.replace(/\s+/g, '_')}_${Date.now()}`,
              wellName: name,
              operator: 'Community Facility',
              api: '',
              latitude: lat,
              longitude: lng,
              county: '',
              status: 'Active',
              type: 'Other',
              basin: getCachedBasinForPoint(lat, lng) || 'Unknown'
            };
            addFavoriteToStore(favoriteWell);
            setShowFavoritesMap(true);
            telegram.hapticFeedback('medium');
          }}
        />
        
        {/* Toast for cache -> live fallback */}
        <Snackbar
          open={toast.open}
          autoHideDuration={2800}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleToastClose} severity="info" sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
        
        {/* Onboarding Dialog */}
        <OnboardingDialog
          open={showOnboarding}
          onComplete={() => {
            setShowOnboarding(false);
            localStorage.setItem('hasSeenOnboarding', 'true');
          }}
        />
        
        {/* Navigation Drawer */}
        <Drawer
          anchor="left"
          open={openDrawer}
          onClose={() => { telegram.hapticFeedback('light'); setOpenDrawer(false); }}
          PaperProps={{
            sx: {
              backgroundColor: cyber.bgCard,
              backgroundImage: 'none',
              width: '280px',
              border: `1px solid ${cyber.borderHover}`,
              boxShadow: cyber.shadowElevated
            }
          }}
        >
          <Box sx={{ p: 2, pt: 4, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
            {/* Drawer Title - Just Close Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
              <IconButton
                onClick={() => { telegram.hapticFeedback('light'); setOpenDrawer(false); }}
                sx={{ color: cyber.neonBlue, width: 48, height: 48 }}
              >
                <CloseIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
            
            <Divider sx={{ backgroundColor: cyber.borderDefault, mb: 3 }} />

            {/* Offline Cache - Opens OfflineCacheManager */}
            <List sx={{ p: 0, mb: 2 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setShowOfflineCacheManager(true);
                    setOpenDrawer(false);
                  }}
                  sx={{
                    py: 2,
                    px: 2,
                    color: cyber.textPrimary,
                    '&:hover': {
                      backgroundColor: `${cyber.neonCyan}10`
                    },
                    borderRadius: 1,
                    border: `1px solid ${cyber.neonCyan}30`,
                    background: `linear-gradient(135deg, ${cyber.neonCyan}05, ${cyber.neonBlue}05)`
                  }}
                >
                  <CloudDownloadIcon sx={{ mr: 2, color: cyber.neonCyan, flexShrink: 0 }} />
                  <ListItemText
                    primary="Offline Cache Manager"
                    secondary="Download maps & facility data"
                    primaryTypographyProps={{
                      color: cyber.textPrimary,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      noWrap: true
                    }}
                    secondaryTypographyProps={{
                      color: cyber.textMuted,
                      fontSize: '0.7rem',
                      noWrap: true
                    }}
                    sx={{ overflow: 'hidden' }}
                  />
                  <ExpandMoreIcon sx={{ color: cyber.neonCyan, transform: 'rotate(-90deg)' }} />
                </ListItemButton>
              </ListItem>
            </List>

            {/* Help Section - Collapsible */}
            <Divider sx={{ backgroundColor: cyber.borderDefault, my: 2 }} />
            <ListItemButton
              onClick={() => setExpandHelp(!expandHelp)}
              sx={{
                px: 0,
                py: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HelpOutlineIcon sx={{ color: cyber.neonCyan, fontSize: 20 }} />
                <Typography variant="caption" sx={{ color: cyber.textSecondary, fontWeight: 600 }}>
                  HELP & TIPS
                </Typography>
              </Box>
              {expandHelp ? (
                <ExpandLessIcon sx={{ color: cyber.neonCyan, fontSize: 20 }} />
              ) : (
                <ExpandMoreIcon sx={{ color: cyber.neonCyan, fontSize: 20 }} />
              )}
            </ListItemButton>

            {expandHelp && (
            <Box sx={{ 
              mt: 1,
              maxHeight: 400,
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { 
                backgroundColor: `${cyber.neonCyan}40`,
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb:hover': { 
                backgroundColor: `${cyber.neonCyan}60`,
              },
            }}>
              {/* Voice Search */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonBlue}08`,
                border: `1px solid ${cyber.neonBlue}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MicIcon sx={{ color: cyber.neonBlue, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    {t('voiceSearchTitle')}
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  {t('voiceSearchIOS')}
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5, mt: 0.5 }}>
                  {t('voiceSearchAndroid')}
                </Typography>
              </Box>

              {/* Road Hazards - NEW */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonOrange}08`,
                border: `1px solid ${cyber.neonOrange}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon sx={{ color: cyber.neonOrange, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Road Hazards
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Report 8 hazard types: crashes, slowdowns, construction, lane closures, objects, floods, low visibility, and cops.
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5, mt: 0.5 }}>
                  <strong>🐻 Bear Alerts:</strong> Police sightings go "ghost" after 30min. When you pass one, confirm YES/NO if still there!
                </Typography>
              </Box>

              {/* FAB Buttons */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonPurple}08`,
                border: `1px solid ${cyber.neonPurple}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TouchAppIcon sx={{ color: cyber.neonPurple, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    {t('fabButtonsTitle')}
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  {t('tapToOpen')}
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  {t('longPressToDrag')}
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  {t('moveFabsBetweenZones')}
                </Typography>
              </Box>

              {/* Wells Near You */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonGreen}08`,
                border: `1px solid ${cyber.neonGreen}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationOnIcon sx={{ color: cyber.neonGreen, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Wells Near You
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  139,000+ wells & 9,440 OCD facilities. Tap "Near Me" to find closest locations by type with live distance updates.
                </Typography>
              </Box>

              {/* Favorites */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonGold}08`,
                border: `1px solid ${cyber.neonGold}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StarIcon sx={{ color: cyber.neonGold, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Favorites
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Tap the star icon on any facility to save it. View all favorites from the ⭐ FAB or map view.
                </Typography>
              </Box>

              {/* Crews */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonPink}08`,
                border: `1px solid ${cyber.neonPink}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GroupIcon sx={{ color: cyber.neonPink, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Crews
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Create or join a crew to share locations, routes, and coordinate with your team. GPS sharing is optional.
                </Typography>
              </Box>

              {/* Offline Mode */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.textMuted}08`,
                border: `1px solid ${cyber.textMuted}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WifiOffIcon sx={{ color: cyber.textSecondary, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Offline Mode
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Auto-downloads all 139,000+ locations on first visit. Works without internet in the field!
                </Typography>
              </Box>

              {/* Weather & Hazards */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonBlue}08`,
                border: `1px solid ${cyber.neonBlue}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CloudIcon sx={{ color: cyber.neonBlue, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Weather Alerts
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Flash flood warnings, severe weather, and road conditions. Critical for monsoon season safety.
                </Typography>
              </Box>

              {/* Privacy */}
              <Box sx={{ 
                p: 2, 
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${cyber.neonGreen}08`,
                border: `1px solid ${cyber.neonGreen}30`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LockIcon sx={{ color: cyber.neonGreen, fontSize: 20 }} />
                  <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                    Privacy First
                  </Typography>
                </Box>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  No account required. E2E encryption via Telegram. Your location is never stored on our servers.
                </Typography>
              </Box>

              {/* Glove-Friendly */}
              <Box sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: `${cyber.neonPink}08`,
                border: `1px solid ${cyber.neonPink}30`
              }}>
                <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                  🧤 Glove-Friendly
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  All buttons are 48px+ for easy tapping with work gloves. Touch targets optimized for oilfield use.
                </Typography>
              </Box>

              {/* Telegram Expand Tip */}
              <Box sx={{ 
                p: 2, 
                borderRadius: 2,
                backgroundColor: `${cyber.neonCyan}08`,
                border: `1px solid ${cyber.neonCyan}30`
              }}>
                <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                  📱 Expand App
                </Typography>
                <Typography sx={{ color: cyber.textMuted, fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Drag the top/bottom edge or corners of the app to expand ZiaWellNav to full screen in Telegram.
                </Typography>
              </Box>


            </Box>
            )}
          </Box>
        </Drawer>

        {/* Add Facility Form */}
        {addFacilityLocation && (
          <AddFacilityForm
            open={showAddFacility}
            onClose={() => {
              setShowAddFacility(false);
              setAddFacilityLocation(null);
            }}
            latitude={addFacilityLocation.lat}
            longitude={addFacilityLocation.lng}
            onSuccess={() => {
              setToast({ open: true, message: 'Facility added and now visible on the map!' });
            }}
            onViewExistingFacility={(facility) => {
              // Close the add form
              setShowAddFacility(false);
              setAddFacilityLocation(null);
              // Search for the facility by API number
              setEditableQuery(facility.api);
              handleSearchCommand(facility.api);
            }}
            onViewOnMap={(lat, lng, facilityName) => {
              // Close the add form
              setShowAddFacility(false);
              setAddFacilityLocation(null);
              // Create a temporary well object
              const newFacility: Well = {
                id: `new-facility-${Date.now()}`,
                api: '',
                wellName: facilityName,
                latitude: lat,
                longitude: lng,
                county: '',
                operator: '',
                status: 'active',
                basin: '',
                type: 'facility'
              };
              // Add to favorites and show on map
              addFavoriteToStore(newFacility);
              setSelectedWell(newFacility);
              setShowFavoritesMap(true);
            }}
            onAddToFavorites={(facility) => {
              // Create a Well object from the submitted facility
              const wellToFavorite: Well = {
                id: facility.id,
                api: facility.api || '',
                wellName: facility.name,
                latitude: facility.latitude,
                longitude: facility.longitude,
                county: facility.county || '',
                operator: facility.operator || '',
                status: 'active',
                basin: '',
                type: facility.facilityType
              };
              addToFavorites(wellToFavorite);
            }}
            onOpenCommunityFacilities={() => {
              setShowCommunityFacilities(true);
            }}
          />
        )}

        {/* Photo Routes - Crowdsourced Route Photos (deprecated/legacy) */}
        <CommunityRoutes
          open={showCommunityRoutes}
          onClose={() => setShowCommunityRoutes(false)}
        />

        {/* Facilities View Panel - Browse all community facilities */}
        <FacilitiesViewPanel
          open={showFacilitiesView}
          onClose={() => setShowFacilitiesView(false)}
          onNavigate={(lat, lng, name) => {
            const favoriteWell: Well = {
              id: `facility_${name.replace(/\s+/g, '_')}_${Date.now()}`,
              wellName: name,
              operator: 'Community Facility',
              api: '',
              latitude: lat,
              longitude: lng,
              county: '',
              status: 'Active',
              type: 'Other',
              basin: getCachedBasinForPoint(lat, lng) || 'Unknown'
            };
            addFavoriteToStore(favoriteWell);
            setShowFavoritesMap(true);
            telegram.hapticFeedback('medium');
          }}
          onAddPhoto={(facility) => {
            setFacilityForPhoto(facility);
            setShowFacilityPhotoUploader(true);
            telegram.hapticFeedback('light');
          }}
        />

        {/* Facility Photo Uploader */}
        {facilityForPhoto && (
          <PhotoUploader
            open={showFacilityPhotoUploader}
            onClose={() => {
              setShowFacilityPhotoUploader(false);
              setFacilityForPhoto(null);
            }}
            facilityId={facilityForPhoto.id}
            facilityType={facilityForPhoto.type}
            onSuccess={() => {
              telegram.hapticFeedback('success');
              setShowFacilityPhotoUploader(false);
              setFacilityForPhoto(null);
            }}
          />
        )}

        {/* Hazards View Panel - Browse all reported hazards */}
        <HazardsViewPanel
          open={showHazardsView}
          onClose={() => setShowHazardsView(false)}
          hazards={roadHazards}
          onNavigate={(lat, lng, name) => {
            const favoriteWell: Well = {
              id: `hazard_${name.replace(/\s+/g, '_')}_${Date.now()}`,
              wellName: name,
              operator: 'Road Hazard',
              api: '',
              latitude: lat,
              longitude: lng,
              county: '',
              status: 'Active',
              type: 'Other',
              basin: getCachedBasinForPoint(lat, lng) || 'Unknown'
            };
            addFavoriteToStore(favoriteWell);
            setShowFavoritesMap(true);
            telegram.hapticFeedback('medium');
          }}
          onVote={async (hazardId, vote) => {
            try {
              const userId = telegram.getUserId() || 'anonymous';
              const voteType = vote === 1 ? 'still_there' : 'gone';
              await roadHazardsService.voteOnHazard(hazardId, voteType, userId);
              telegram.hapticFeedback('light');
            } catch (error) {
              console.error('Failed to vote on hazard:', error);
            }
          }}
          onAddPhoto={(hazard) => {
            setFacilityForPhoto({
              id: hazard.id,
              name: hazard.name,
              type: hazard.type,
              latitude: hazard.latitude,
              longitude: hazard.longitude
            });
            setShowFacilityPhotoUploader(true);
            telegram.hapticFeedback('light');
          }}
        />

        {/* Caliche Routes Panel - Landmark-based routes to facilities */}
        <CommunityRoutesPanel
          open={showCaliches}
          onClose={() => setShowCaliches(false)}
          refreshKey={calicheRoutesRefreshKey}
          onAddPhoto={(route) => {
            setFacilityForPhoto({
              id: route.id,
              name: route.name,
              type: route.type,
              latitude: 0,
              longitude: 0
            });
            setShowFacilityPhotoUploader(true);
            telegram.hapticFeedback('light');
          }}
        />

        {/* Online/Offline Status Bubble - REMOVED per user request */}

        {/* Road Hazard Reporting - Now handled directly in UnifiedFabManager */}

        {/* Suggestions Dialog */}
        <SuggestionsDialog
          open={showSuggestions}
          onClose={() => setShowSuggestions(false)}
        />

        {/* QR Share / Referral Dialog */}
        <QRShareDialog
          open={showQRShare}
          onClose={() => setShowQRShare(false)}
        />

        {/* Zia Gives Back - Charity Dashboard */}
        <CharityDashboard
          open={showCharityDashboard}
          onClose={() => setShowCharityDashboard(false)}
        />

        {/* Unified Offline Cache Manager (Map Tiles + Facility Data) */}
        <OfflineCacheManager
          open={showOfflineCacheManager}
          onClose={() => setShowOfflineCacheManager(false)}
          darkMode={darkMode}
        />

        {/* Subscription/Premium Panel */}
        <SubscriptionPanel
          open={showSubscription}
          onClose={() => setShowSubscription(false)}
          onSubscribed={async () => {
            // Refresh subscription status after subscribing
            const status = await subscriptionService.getStatus(true);
            setSubscriptionStatus(status);
            setShowSubscription(false);
          }}
        />

        {/* VPN Warning Snackbar - Temporarily disabled */}
        {/* <VPNWarningSnackbar /> */}

        {/* Draw Over Apps Prompt (Android Telegram) */}
        <DrawOverAppsPrompt
          open={showDrawOverAppsPrompt}
          onClose={dismissDrawOverAppsPrompt}
          onComplete={dismissDrawOverAppsPrompt}
        />

        {/* Location Permission Dialog */}
        <LocationPermissionDialog
          open={locationPermission.showDialog}
          onClose={locationPermission.handleClose}
          onPermissionGranted={locationPermission.handlePermissionGranted}
          onPermissionDenied={locationPermission.handlePermissionDenied}
          featureName={locationPermission.featureName}
        />

        {/* Location Permission Expiry Reminder */}
        <Snackbar
          open={expiryWarning.show}
          autoHideDuration={30000}
          onClose={dismissExpiryWarning}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            onClose={dismissExpiryWarning}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={requestLocationAgain}
                sx={{ fontWeight: 'bold' }}
              >
                EXTEND
              </Button>
            }
            sx={{ 
              width: '100%',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              border: '1px solid #3b82f6',
              color: '#f1f5f9',
              '& .MuiAlert-icon': { color: '#3b82f6' }
            }}
          >
            📍 Location access expires in {expiryWarning.minutesRemaining} min
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
